import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import MuleBadge from '../../components/MuleBadge';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronRightIcon, ChevronUpIcon, ChevronDownIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import ordersService from '../../services/orders.service';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import InfoModal from '../../components/common/InfoModal';
import Toggle from '../../components/Toggle';
import { Tooltip } from '../../components/ui/Tooltip';
import ProDetailsDrawer from '../../components/ProDetailsDrawer';
import DriverPreferencesBans from './DriverPreferencesBans';
import DriverPlanning from './DriverPlanning';
import ParkingLocationModal, { getParkingColorStyles } from '../../components/ParkingLocationModal';
import DriverPlanningWeek from './DriverPlanningWeek';
import DriverGroundedYard from './DriverGroundedYard';
import DriverDeclinedMoves from './DriverDeclinedMoves';
import DateTimePicker from '../../components/DateTimePicker';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import { usePermissions } from '../../contexts/PermissionContext';
import GridLabel from '../../components/GridLabel';
import FilterBar from '../../components/FilterBar';
import { DriverDispatcherFilter } from '../../components/filters';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import TypeAhead from '../../components/TypeAhead';
import {
  WORKING_CATEGORIES,
  getWorkingCategoryLabel,
  WORKING_CATEGORY_LIVE_DISPATCH,
  WORKING_CATEGORY_GROUNDED_YARD,
  WORKING_CATEGORY_DONE_FOR_DAY,
} from '../../constants/workingCategories';
import { useWorkingStatusPreference } from '../../contexts/WorkingStatusPreferenceContext';
import DriverPhoneLink from '../../components/DriverPhoneLink';
import DriverStatusDot from '../../components/DriverStatusDot';
import TagBadge from '../../components/TagBadge';
import {
  DRIVER_QUALIFICATIONS,
  getQualStatStyle,
  getQualificationColor,
  hasAllQualifications,
  parseQualifications,
  qualificationAbbr,
  qualificationOptions,
} from '../../constants/driverQualifications';
import styles from './Drivers.module.css';
import userSettingsService from '../../services/userSettings.service';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { matchesDriverPreference } from '../../utils/driverPreferenceFilter';
import { config } from '../../config/config';

interface StatusInfo {
  status: string;
  workingcategory?: string;
  comments: string;
  changed_by: string;
  changed_datetime?: string;
  has_conflict?: boolean;
  assigned_pros?: string[];
  is_overdue?: boolean;
  return_date?: string;
  oos_comments?: string;
  oos_type?: string;
  confirmed?: boolean;
}

interface DriverStatus {
  driver: string;
  driver_name: string;
  isMule?: boolean;
  preference: string;  // OTR or LOCAL
  parkingloc: number | null;
  parking_location_name: string;
  parking_color: number | null;
  region: string;
  statuses: Record<string, StatusInfo>; // date string -> status info
  textmsgnumber?: string;
  homephone?: string;
  endorsements?: string | null;
}

interface DispatcherGroup {
  dispatcher: string;
  region: string;
  drivers: DriverStatus[];
}

// The Quals cell is flex-nowrap inside an overflow-hidden td, so this width is a
// hard clip, not a wrap point. A fully-qualified driver now carries seven badges
// (H T 93 S -15 A Tw), which measures ~123px at the 10px badge size -- 100px used
// to be enough at six and silently cut the last one off. Revisit if a badge is added.
const WORKING_QUALS_COL_WIDTH = 130;

export const getStatusStyle = (status: string | undefined, statusInfo?: StatusInfo, region?: string) => {
  if (!status) return '';

  // Handle (OOS) suffix statuses
  if (status.includes('(OOS)')) {
    return 'bg-green-600 text-white';
  }

  switch (status) {
    case 'Planned':
    case 'Planned Accepted':
      // Grounded Yard / Do Not Preplan / Done For The Day keep their category
      // color on planned days too — confirmed or not (2026-09-08) — so a
      // planned category driver never flips to yellow/green and hides the
      // category. Done For The Day matters most here: a finished driver
      // nearly always has legs, so Available is the one status he is NOT in.
      // In Route OTR keeps the plain Planned palette.
      if (statusInfo?.workingcategory === WORKING_CATEGORY_LIVE_DISPATCH) {
        return 'bg-orange-100 text-orange-700';
      }
      if (statusInfo?.workingcategory === WORKING_CATEGORY_GROUNDED_YARD) {
        return 'bg-purple-100 text-purple-700';
      }
      if (statusInfo?.workingcategory === WORKING_CATEGORY_DONE_FOR_DAY) {
        return 'bg-sky-300 text-sky-900';
      }
      // Check if not confirmed
      if (statusInfo && !statusInfo.confirmed) {
        return 'bg-yellow-400 text-yellow-900'; // Yellow for unconfirmed drivers
      }
      return 'bg-green-600 text-white';
    case 'Available':
    case 'WORKING':
      // Category color outranks the unconfirmed yellow: dispatchers almost
      // never confirm category drivers (0 of 141 Grounded Yard rows one
      // sampled week), so yellow-first made these colors unreachable.
      // "Do Not Preplan" is stored as 'Live Dispatch' — see workingCategories.ts
      if (statusInfo?.workingcategory === WORKING_CATEGORY_LIVE_DISPATCH) {
        return 'bg-orange-100 text-orange-700';
      }
      if (statusInfo?.workingcategory === WORKING_CATEGORY_GROUNDED_YARD) {
        return 'bg-purple-100 text-purple-700';
      }
      // Done For The Day: worked today, taking no more work. Sky blue on
      // purpose — NOT the Not Working blue (bg-info = blue-100), so a finished
      // driver never reads as one who never worked.
      if (statusInfo?.workingcategory === WORKING_CATEGORY_DONE_FOR_DAY) {
        return 'bg-sky-300 text-sky-900';
      }
      // Check if not confirmed
      if (statusInfo && !statusInfo.confirmed) {
        return 'bg-flag text-flag-fg'; // Yellow for unconfirmed drivers
      }
      return 'bg-green-100 text-green-700';
    case 'Out of Service':
    case 'Out Of Service':
      return 'bg-danger text-danger-fg';
    case 'Pending Return':
      return 'bg-red-50 text-red-700';
    case 'Not Working':
    case 'NOT WORKING':
      return 'bg-info text-info-fg';
    case 'Unknown':
      return 'text-gray-400';
    default:
      return '';
  }
};

const getDisplayStatus = (status: string | undefined) => {
  if (!status) return status;
  if (status === 'WORKING') return 'Available';
  if (status === 'NOT WORKING') return 'Not Working';
  if (status === 'Planned Accepted (OOS)') return 'Accepted (OOS)';
  if (status === 'Planned (OOS)') return 'Planned (OOS)';
  if (status === 'Planned Accepted') return 'Accepted';
  if (status === 'Planned') return 'Planned';
  return status;
};

// Convert name to title case (camel case)
const toTitleCase = (str: string | undefined) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Sort drivers within a group by saved order, with unsaved drivers at the end
const sortDriversByOrder = (
  drivers: DriverStatus[],
  savedOrder: string[] | undefined
): DriverStatus[] => {
  if (!savedOrder || savedOrder.length === 0) {
    return drivers;
  }

  const orderMap = new Map(savedOrder.map((id, index) => [id, index]));

  return [...drivers].sort((a, b) => {
    const aIndex = orderMap.get(a.driver);
    const bIndex = orderMap.get(b.driver);

    // Both have saved positions - sort by saved order
    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }
    // Only a has saved position - a comes first
    if (aIndex !== undefined) return -1;
    // Only b has saved position - b comes first
    if (bIndex !== undefined) return 1;
    // Neither has saved position - maintain original order
    return 0;
  });
};

// Get Monday of the week containing a date
const getMondayOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// localStorage key for persisting collapsed days on the Working/Not Working tab
const WORKING_COLLAPSED_DAYS_STORAGE_KEY = 'drivers_working_collapsedDays';

const Drivers: React.FC = () => {
  const { setGlobalFilters, setDispatchButton } = useDispatchFooter();
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();
  const { showExtendedWorkingStatus, planningShowTwic, setPlanningShowTwic } = useWorkingStatusPreference();
  const { permissions } = usePermissions();
  const [activeTab, setActiveTab] = useState<'working' | 'preferences' | 'planning' | 'planning-week' | 'grounded-yard' | 'declined-moves'>('working');
  useFavoriteTab(activeTab, setActiveTab, ['working', 'preferences', 'planning', 'planning-week', 'grounded-yard', 'declined-moves']);
  const [data, setData] = useState<DispatcherGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDispatchers, setExpandedDispatchers] = useState<Set<string>>(new Set());

  // Global filters (shared across pages) - selectedDispatchers is Driver Dispatcher here
  const {
    selectedRegions,
    setSelectedRegions,
    selectedDriverDispatchers: selectedDispatchers,
    setSelectedDriverDispatchers: setSelectedDispatchers,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  // Local filters (page-specific, but still persisted)
  const [selectedDriverType, setSelectedDriverType] = useState<string[]>([]);
  // Planning Week only: free-text delivery location filter (not persisted)
  const [deliveryLocationQuery, setDeliveryLocationQuery] = useState('');
  const localFiltersLoaded = useRef(false);

  // Load local filters from user settings
  useEffect(() => {
    if (localFiltersLoaded.current) return;
    const loadLocalFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('drivers_page_filters');
        if (setting?.setting_value) {
          const filters = setting.setting_value;
          // Upper-case legacy persisted values ('Local') so they match the
          // current option values and light up the chips/checkmarks.
          if (filters.selectedDriverType) {
            setSelectedDriverType(
              (filters.selectedDriverType as string[]).map(type => type.toUpperCase())
            );
          }
        }
      } catch (error) {
        // Silently fail
      }
      localFiltersLoaded.current = true;
    };
    loadLocalFilters();
  }, []);

  // Auto-save local filters when they change (debounced)
  const saveLocalFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!localFiltersLoaded.current) return;

    if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    saveLocalFiltersTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting('drivers_page_filters', { selectedDriverType });
      } catch (error) {
        console.error('Failed to save local filters:', error);
      }
    }, 500);

    return () => {
      if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    };
  }, [selectedDriverType]);

  const [columnFilters, setColumnFilters] = useState<Record<number, string[]>>({});
  const [qualificationsFilter, setQualificationsFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [planningDate, setPlanningDate] = useState<Date | null>(new Date());
  const [groundedYardDate, setGroundedYardDate] = useState<Date | null>(new Date());
  const [declinedMovesDate, setDeclinedMovesDate] = useState<Date | null>(new Date());
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const fetchIdRef = useRef(0); // Counter to prevent stale auto-refresh responses from overwriting fresh data
  // Ref, NOT state: as state (and a dep of the fetch effect) the flag's flip
  // after the first load re-ran the effect, so every page load fetched twice.
  const initialLoadCompleteRef = useRef(false);
  const [groupByDispatcher, setGroupByDispatcher] = useState(true);
  // Week View only: sub-group each dispatcher's drivers into LOCAL / OTR blocks
  const [groupByPreference, setGroupByPreference] = useState(false);
  const [showStatus, setShowStatus] = useState(true);
  const [showAppointmentTimes, setShowAppointmentTimes] = useState(false);
  const [showQualifications, setShowQualifications] = useState(false);
  const [showWorkingQuals, setShowWorkingQuals] = useState(true);

  // Font size (zoom) control - persisted per user
  const [pageZoom, setPageZoom] = useState(() => {
    const saved = localStorage.getItem('drivers_page_zoom');
    return saved ? Number(saved) : 100;
  });
  const pageZoomLoaded = useRef(false);

  useEffect(() => {
    if (pageZoomLoaded.current) return;
    const loadZoom = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('drivers_page_zoom');
        if (setting?.setting_value != null) {
          const val = typeof setting.setting_value === 'number' ? setting.setting_value : Number(setting.setting_value);
          if (!isNaN(val)) {
            setPageZoom(val);
            localStorage.setItem('drivers_page_zoom', val.toString());
          }
        }
      } catch {
        // Use localStorage value
      }
      pageZoomLoaded.current = true;
    };
    loadZoom();
  }, []);

  const handleZoomChange = useCallback((newZoom: number) => {
    const clamped = Math.max(70, Math.min(130, newZoom));
    setPageZoom(clamped);
    localStorage.setItem('drivers_page_zoom', clamped.toString());
    userSettingsService.saveUserSetting('drivers_page_zoom', clamped).catch(() => {});
  }, []);

  // Driver sort order: { dispatcherName: [driverId1, driverId2, ...] }
  const [driverSortOrder, setDriverSortOrder] = useState<Record<string, string[]>>({});

  // Column sort state: column key and direction
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Drag state for native HTML5 drag and drop
  const [draggedDriver, setDraggedDriver] = useState<{ id: string; dispatcher: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: 'before' | 'after' } | null>(null);

  // Load driver sort order from user settings
  useEffect(() => {
    const loadSortOrder = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('drivers_sort_order');
        if (setting?.setting_value) {
          setDriverSortOrder(setting.setting_value);
        }
      } catch (error) {
        console.error('Failed to load driver sort order:', error);
      }
    };
    loadSortOrder();
  }, []);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDriver, setModalDriver] = useState<string>('');
  const [modalDriverName, setModalDriverName] = useState<string>('');
  const [modalDate, setModalDate] = useState<string>('');
  const [modalRegion, setModalRegion] = useState<string>(''); // For checking if port terminal
  const [modalWorking, setModalWorking] = useState<'UNKNOWN' | 'WORKING' | 'NOT_WORKING'>('WORKING');
  const [modalWorkingCategory, setModalWorkingCategory] = useState<string>('');
  const [modalComment, setModalComment] = useState('');
  const [modalConfirmed, setModalConfirmed] = useState(false); // Houston only
  const [modalChangedBy, setModalChangedBy] = useState<string>(''); // Last changed by
  const [modalChangedDateTime, setModalChangedDateTime] = useState<string>(''); // Last changed datetime
  const [modalHasExistingStatus, setModalHasExistingStatus] = useState(false); // Whether there's an existing status entry
  const [saving, setSaving] = useState(false);

  // Personal driver notes (private to the logged-in user, keyed by driver unit number).
  // Surfaced via the same notes icon/tooltip; clicking the icon opens the notes modal.
  const [driverNotes, setDriverNotes] = useState<Record<string, string>>({});
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesModalDriver, setNotesModalDriver] = useState<string>('');
  const [notesModalDriverName, setNotesModalDriverName] = useState<string>('');
  const [notesModalText, setNotesModalText] = useState<string>('');
  const [notesSaving, setNotesSaving] = useState(false);

  // Bulk edit state
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<'NOT_WORKING' | 'UNKNOWN' | 'WORKING'>('UNKNOWN');
  const [bulkWorkingCategory, setBulkWorkingCategory] = useState<string>('');
  const [bulkComment, setBulkComment] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartCell, setDragStartCell] = useState<string | null>(null);

  // Drawer state for showing driver legs
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerDriver, setDrawerDriver] = useState<string>('');
  const [drawerDriverName, setDrawerDriverName] = useState<string>('');
  const [drawerDate, setDrawerDate] = useState<string>('');
  const [drawerLegs, setDrawerLegs] = useState<any[]>([]);
  const [loadingLegs, setLoadingLegs] = useState(false);
  const [drawerConfirmed, setDrawerConfirmed] = useState<boolean>(false);
  const [drawerChangedBy, setDrawerChangedBy] = useState<string>('');
  const [drawerChangedDateTime, setDrawerChangedDateTime] = useState<string>('');
  const [drawerHasExistingStatus, setDrawerHasExistingStatus] = useState<boolean>(false);
  const [drawerComment, setDrawerComment] = useState<string>('');
  const [drawerStatus, setDrawerStatus] = useState<string | null>(null);
  const [drawerWorkingCategory, setDrawerWorkingCategory] = useState<string | null>(null);

  // Order Details drawer state
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  // Parking location modal state
  const [parkingModalOpen, setParkingModalOpen] = useState(false);
  const [parkingModalDriver, setParkingModalDriver] = useState<string>('');
  const [parkingModalDriverName, setParkingModalDriverName] = useState<string>('');
  const [parkingModalCurrentLoc, setParkingModalCurrentLoc] = useState<number | null>(null);
  const [parkingModalCurrentName, setParkingModalCurrentName] = useState<string>('');
  const [parkingModalCurrentColor, setParkingModalCurrentColor] = useState<number | null>(null);

  // Current week's Monday (minimum allowed)
  const currentWeekMonday = useMemo(() => getMondayOfWeek(new Date()), []);

  const [weekStart, setWeekStart] = useState<Date>(() => getMondayOfWeek(new Date()));

  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }, [weekStart]);

  // Collapsed-day state for the Working/Not Working tab. Persisted to localStorage
  // so the user's choices survive reloads. Past days auto-collapse on week change.
  const [collapsedDays, setCollapsedDays] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem(WORKING_COLLAPSED_DAYS_STORAGE_KEY);
      if (stored) return new Set(JSON.parse(stored));
    } catch {
      // ignore localStorage errors
    }
    return new Set();
  });

  useEffect(() => {
    const today = (() => {
      const d = new Date();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })();
    const newCollapsed = new Set<number>();
    weekDays.forEach((day, index) => {
      const dateKey = formatDateKey(day);
      if (dateKey < today) newCollapsed.add(index);
    });
    setCollapsedDays(newCollapsed);
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      localStorage.setItem(WORKING_COLLAPSED_DAYS_STORAGE_KEY, JSON.stringify([...collapsedDays]));
    } catch {
      // ignore localStorage errors
    }
  }, [collapsedDays]);

  const toggleDayCollapsed = (dayIndex: number) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayIndex)) next.delete(dayIndex);
      else next.add(dayIndex);
      return next;
    });
  };

  // Username to displayname map for filtering (API returns displayname as group.dispatcher)
  const [usernameToDisplayname, setUsernameToDisplayname] = useState<Map<string, string>>(new Map());

  // Fetch driver dispatchers to build username -> displayname map
  useEffect(() => {
    const fetchDriverDispatchers = async () => {
      try {
        const response = await ordersService.getDriverDispatchers();
        const map = new Map<string, string>();
        response.data.forEach((d: any) => {
          map.set(d.username, d.displayname);
        });
        setUsernameToDisplayname(map);
      } catch (err) {
        console.error('Failed to fetch driver dispatchers:', err);
      }
    };
    fetchDriverDispatchers();
  }, []);

  // Build region options for filter
  const regionOptions: MultiSelectOption[] = useMemo(() => {
    const regions = new Set<string>();
    data.forEach(group => {
      group.drivers.forEach(driver => {
        if (driver.region) {
          regions.add(driver.region);
        }
      });
    });
    return Array.from(regions).sort().map(region => ({
      value: region,
      label: region,
    }));
  }, [data]);

  // Auto-select user's home region when available (only if no global region is set)
  const homeRegionInitialized = useRef(false);
  useEffect(() => {
    if (
      !homeRegionInitialized.current &&
      !globalFiltersLoading &&
      selectedRegions.length === 0 &&
      permissions?.home_region &&
      permissions?.home_region_as_default !== false &&
      regionOptions.length > 0 &&
      regionOptions.some(opt => opt.value === permissions.home_region)
    ) {
      setSelectedRegions([permissions.home_region]);
      homeRegionInitialized.current = true;
    }
  }, [permissions?.home_region, permissions?.home_region_as_default, regionOptions, globalFiltersLoading, selectedRegions.length, setSelectedRegions]);

  // Driver type options for Local/OTR filter
  const driverTypeOptions: MultiSelectOption[] = [
    { value: 'LOCAL', label: 'Local' },  // value matches DB, label is display
    { value: 'OTR', label: 'OTR' }
  ];

  // Calculate status counts for each day (using statsFilteredData to exclude column filters)
  const getStatusCountsForDay = (dayIndex: number) => {
    const dateKey = formatDateKey(weekDays[dayIndex]);
    const counts: Record<string, number> = {
      'Available': 0,
      'Not Working': 0,
      'Out Of Service': 0,
      'Unknown': 0,
      'Planned': 0,
      'Planned Accepted': 0,
      'Planned Accepted Confirmed': 0,
    };
    WORKING_CATEGORIES.forEach(c => { counts[c.value] = 0; });

    statsFilteredData.forEach(group => {
      group.drivers.forEach(driver => {
        const statusInfo = driver.statuses[dateKey];
        const status = statusInfo?.status;
        const workingCategory = statusInfo?.workingcategory;
        const confirmed = statusInfo?.confirmed;

        if (status) {
          // Special case: Available + a working category counts toward that category
          if (status === 'Available' && workingCategory && counts.hasOwnProperty(workingCategory)) {
            counts[workingCategory]++;
          }
          // Special case: Planned Accepted - split by confirmed status
          else if (status === 'Planned Accepted' || status === 'Planned Accepted (OOS)') {
            if (confirmed) {
              counts['Planned Accepted Confirmed']++;
            } else {
              counts['Planned Accepted']++;
            }
          }
          // Handle exact matches
          else if (counts.hasOwnProperty(status)) {
            counts[status]++;
          }
          // Handle OOS variants - count them with their base status
          else if (status.endsWith(' (OOS)')) {
            const baseStatus = status.replace(' (OOS)', '');
            if (counts.hasOwnProperty(baseStatus)) {
              counts[baseStatus]++;
            }
          }
        }
      });
    });

    return counts;
  };

  // Status filter options (base options, counts added per column).
  // Working categories are inserted after Available so they sort with related
  // statuses, and stay driven by the centralized list.
  const baseStatusOptions = [
    { value: 'Available', label: 'Available' },
    ...WORKING_CATEGORIES.map(c => ({ value: c.value, label: c.label })),
    { value: 'Planned', label: 'Planned' },
    { value: 'Planned Accepted', label: 'Accepted' },
    { value: 'Planned Accepted Confirmed', label: 'Accepted Confirmed' },
    { value: 'Not Working', label: 'Not Working' },
    { value: 'Out Of Service', label: 'Out of Service' },
    { value: 'Unknown', label: 'Unknown' },
  ];

  // Helper function to format date as YYYY-MM-DD (UTC)
  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const STATUS_SORT_ORDER: Record<string, number> = {
    'Available': 1,
    'Planned Accepted': 2,
    'Planned Accepted (OOS)': 2,
    'Planned': 3,
    'Planned (OOS)': 3,
    'Not Working': 4,
    'Out Of Service': 5,
    'Pending Return': 6,
    'Unknown': 7,
  };

  const handleColumnSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter data based on selected dispatchers, regions, column status filters, and search
  const filteredData = useMemo(() => {
    let filtered = data;

    if (selectedDispatchers.length > 0) {
      // Expand group values (tilde-separated dispatcher lists) into individual usernames
      const expandedUsernames = selectedDispatchers.flatMap(val => val.includes('~') ? val.split('~') : [val]);
      // Convert usernames to displaynames for comparison (API returns displaynames in group.dispatcher)
      const selectedDisplaynames = new Set(
        expandedUsernames.map(username => usernameToDisplayname.get(username) || username)
      );
      filtered = filtered.filter(group => selectedDisplaynames.has(group.dispatcher));
    }

    if (selectedRegions.length > 0 || selectedDriverType.length > 0 || Object.keys(columnFilters).length > 0 || searchQuery || qualificationsFilter.length > 0) {
      filtered = filtered.map(group => ({
        ...group,
        drivers: group.drivers.filter(driver => {
          // Check search query
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesDriver = driver.driver.toLowerCase().includes(query);
            const matchesName = driver.driver_name?.toLowerCase().includes(query);
            const matchesParkingLoc = driver.parking_location_name?.toLowerCase().includes(query);
            if (!matchesDriver && !matchesName && !matchesParkingLoc) {
              return false;
            }
          }

          // Check region filter
          if (selectedRegions.length > 0 && !selectedRegions.includes(driver.region)) {
            return false;
          }

          // Check driver type (preference) filter
          if (!matchesDriverPreference(driver.preference, selectedDriverType)) {
            return false;
          }

          // Check qualifications filter — driver must have ALL selected qualifications
          if (!hasAllQualifications(driver.endorsements, qualificationsFilter)) {
            return false;
          }

          // Check column filters - must match ALL column filters
          const hasColumnFilters = Object.keys(columnFilters).length > 0;
          if (hasColumnFilters) {
            return Object.entries(columnFilters).every(([dayIndex, statuses]) => {
              if (statuses.length === 0) return true; // No filter for this column
              const dateKey = formatDateKey(weekDays[Number(dayIndex)]);
              const statusInfo = driver.statuses[dateKey];
              const dayStatus = statusInfo?.status;
              const workingCategory = statusInfo?.workingcategory;
              const confirmed = statusInfo?.confirmed;

              // Check if any selected filter matches the status
              // Handle OOS variants: "Planned" should match "Planned (OOS)", etc.
              const workingCategoryValues = WORKING_CATEGORIES.map(c => c.value);
              return statuses.some(filterStatus => {
                // Special case: Available filter - only match if no working category
                if (filterStatus === 'Available') {
                  return dayStatus === 'Available' && (!workingCategory || workingCategory === '');
                }
                // Working-category filters: Available + matching category
                if (workingCategoryValues.includes(filterStatus)) {
                  return dayStatus === 'Available' && workingCategory === filterStatus;
                }
                // Special case: Planned Accepted (not confirmed)
                if (filterStatus === 'Planned Accepted') {
                  const isPlannedAccepted = dayStatus === 'Planned Accepted' || dayStatus === 'Planned Accepted (OOS)';
                  return isPlannedAccepted && !confirmed;
                }
                // Special case: Planned Accepted Confirmed
                if (filterStatus === 'Planned Accepted Confirmed') {
                  const isPlannedAccepted = dayStatus === 'Planned Accepted' || dayStatus === 'Planned Accepted (OOS)';
                  return isPlannedAccepted && confirmed;
                }
                if (dayStatus === filterStatus) return true; // Exact match
                // Check if dayStatus is an OOS variant of the filter
                if (dayStatus === `${filterStatus} (OOS)`) return true;
                return false;
              });
            });
          }

          return true;
        })
      })).filter(group => group.drivers.length > 0);
    }

    // Apply column sort or fall back to custom drag order
    return filtered.map(group => {
      if (sortColumn) {
        const sorted = [...group.drivers].sort((a, b) => {
          let aVal: string | number = '';
          let bVal: string | number = '';
          if (sortColumn === 'driver_name') {
            aVal = a.driver_name?.toLowerCase() || '';
            bVal = b.driver_name?.toLowerCase() || '';
          } else if (sortColumn === 'driver_number') {
            aVal = parseInt(a.driver) || 0;
            bVal = parseInt(b.driver) || 0;
          } else if (sortColumn === 'endorsements') {
            aVal = a.endorsements?.toLowerCase() || '';
            bVal = b.endorsements?.toLowerCase() || '';
          } else if (sortColumn.startsWith('day_')) {
            const dayIndex = parseInt(sortColumn.slice(4));
            const dateKey = formatDateKey(weekDays[dayIndex]);
            const aStatus = a.statuses[dateKey]?.status || '';
            const bStatus = b.statuses[dateKey]?.status || '';
            aVal = STATUS_SORT_ORDER[aStatus] ?? 8;
            bVal = STATUS_SORT_ORDER[bStatus] ?? 8;
          }
          if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
        return { ...group, drivers: sorted };
      }
      return { ...group, drivers: sortDriversByOrder(group.drivers, driverSortOrder[group.dispatcher]) };
    });
  }, [data, selectedDispatchers, selectedRegions, selectedDriverType, columnFilters, qualificationsFilter, searchQuery, weekDays, driverSortOrder, usernameToDisplayname, sortColumn, sortDirection]);

  const workingStats = useMemo(() => {
    const allDrivers = filteredData.flatMap(g => g.drivers);
    const qualCounts: Record<string, number> = {};
    for (const q of DRIVER_QUALIFICATIONS) {
      qualCounts[q] = allDrivers.filter(
        d => parseQualifications(d.endorsements).includes(q),
      ).length;
    }
    return {
      total: allDrivers.length,
      qualCounts,
      otrCount: allDrivers.filter(d => d.preference === 'OTR').length,
      localCount: allDrivers.filter(d => d.preference === 'LOCAL').length,
    };
  }, [filteredData]);

  // Filter data without column filters (for stats calculation)
  const statsFilteredData = useMemo(() => {
    let filtered = data;

    if (selectedDispatchers.length > 0) {
      // Expand group values (tilde-separated dispatcher lists) into individual usernames
      const expandedUsernames = selectedDispatchers.flatMap(val => val.includes('~') ? val.split('~') : [val]);
      // Convert usernames to displaynames for comparison (API returns displaynames in group.dispatcher)
      const selectedDisplaynames = new Set(
        expandedUsernames.map(username => usernameToDisplayname.get(username) || username)
      );
      filtered = filtered.filter(group => selectedDisplaynames.has(group.dispatcher));
    }

    if (selectedRegions.length > 0 || selectedDriverType.length > 0 || searchQuery) {
      filtered = filtered.map(group => ({
        ...group,
        drivers: group.drivers.filter(driver => {
          // Check search query
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesDriver = driver.driver.toLowerCase().includes(query);
            const matchesName = driver.driver_name?.toLowerCase().includes(query);
            const matchesParkingLoc = driver.parking_location_name?.toLowerCase().includes(query);
            if (!matchesDriver && !matchesName && !matchesParkingLoc) {
              return false;
            }
          }

          // Check region filter
          if (selectedRegions.length > 0 && !selectedRegions.includes(driver.region)) {
            return false;
          }

          // Check driver type (preference) filter
          if (!matchesDriverPreference(driver.preference, selectedDriverType)) {
            return false;
          }

          return true;
        })
      })).filter(group => group.drivers.length > 0);
    }

    return filtered;
  }, [data, selectedDispatchers, selectedRegions, selectedDriverType, searchQuery, usernameToDisplayname]);

  // Calculate daily availability statistics (without column filters)
  const dailyStats = useMemo(() => {
    const stats = weekDays.map((date) => {
      const dateKey = formatDateKey(date);
      let availableCount = 0;
      let totalCount = 0;

      statsFilteredData.forEach(group => {
        group.drivers.forEach(driver => {
          totalCount++;
          const status = driver.statuses[dateKey]?.status;
          // Count as available if status is Available, WORKING, Planned, or Planned Accepted (including OOS variants)
          if (status === 'Available' || status === 'WORKING' || status?.includes('Planned')) {
            availableCount++;
          }
        });
      });

      const percentage = totalCount > 0 ? (availableCount / totalCount) * 100 : 0;
      return { availableCount, totalCount, percentage };
    });

    return stats;
  }, [statsFilteredData, weekDays]);

  // Generate week options for dropdown (1 week back + 6 months ahead from current week)
  const weekOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const weeksToShow = 26; // ~6 months
    // Start at -1 to include last week (only 1 week in the past is allowed)
    for (let i = -1; i < weeksToShow; i++) {
      const monday = new Date(currentWeekMonday);
      monday.setDate(currentWeekMonday.getDate() + i * 7);
      const dateKey = monday.toISOString().split('T')[0];
      const label = `Week Of ${monday.getMonth() + 1}/${monday.getDate()}`;
      options.push({ value: dateKey, label });
    }

    // If current weekStart is beyond the 6-month range, add it to options
    const currentWeekKey = weekStart.toISOString().split('T')[0];
    if (!options.find(o => o.value === currentWeekKey)) {
      const label = `Week Of ${weekStart.getMonth() + 1}/${weekStart.getDate()}`;
      options.push({ value: currentWeekKey, label });
    }

    return options;
  }, [currentWeekMonday, weekStart]);

  // Max week (last week in dropdown)
  const maxWeekMonday = useMemo(() => {
    const max = new Date(currentWeekMonday);
    max.setDate(currentWeekMonday.getDate() + 25 * 7); // 26 weeks - 1 (0-indexed)
    return max;
  }, [currentWeekMonday]);

  // Min week (1 week in the past is the furthest back allowed)
  const minWeekMonday = useMemo(() => {
    const min = new Date(currentWeekMonday);
    min.setDate(currentWeekMonday.getDate() - 7);
    return min;
  }, [currentWeekMonday]);

  // Check if we can navigate back (not before last week)
  const canGoBack = useMemo(() => {
    return weekStart.getTime() > minWeekMonday.getTime();
  }, [weekStart, minWeekMonday]);

  // Check if we can navigate forward (not beyond 6 months)
  const canGoForward = useMemo(() => {
    return weekStart.getTime() < maxWeekMonday.getTime();
  }, [weekStart, maxWeekMonday]);

  const formatWeekLabel = (date: Date) => {
    return `Week Of ${date.getMonth() + 1}/${date.getDate()}`;
  };

  const formatDayHeader = (date: Date) => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayInitials = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    return {
      day: dayNames[date.getDay()],
      initial: dayInitials[date.getDay()],
      date: date.getDate(),
    };
  };

  const navigateWeek = (direction: number) => {
    const newStart = new Date(weekStart);
    newStart.setDate(weekStart.getDate() + direction * 7);
    // Don't go before last week (only 1 week in the past; forward is unlimited)
    if (newStart.getTime() >= minWeekMonday.getTime()) {
      setWeekStart(newStart);
    }
  };

  const handleWeekSelect = (dateKey: string) => {
    const newStart = new Date(dateKey + 'T00:00:00');
    setWeekStart(newStart);
  };

  // Native HTML5 drag handlers
  const handleDragStart = useCallback((e: React.DragEvent<HTMLTableRowElement>, driverId: string, dispatcher: string) => {
    // Only allow drag if it started from the drag handle
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle]')) {
      e.preventDefault();
      return;
    }
    setDraggedDriver({ id: driverId, dispatcher });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', driverId);
    // Add visual feedback to dragged row
    (e.target as HTMLElement).style.opacity = '0.5';
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLTableRowElement>) => {
    setDraggedDriver(null);
    setDropTarget(null);
    (e.target as HTMLElement).style.opacity = '1';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLTableRowElement>, driverId: string, dispatcher: string) => {
    e.preventDefault();
    if (!draggedDriver || draggedDriver.dispatcher !== dispatcher || draggedDriver.id === driverId) {
      return;
    }

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? 'before' : 'after';

    setDropTarget({ id: driverId, position });
  }, [draggedDriver]);

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLTableRowElement>, targetDriverId: string, dispatcher: string) => {
    e.preventDefault();
    if (!draggedDriver || draggedDriver.dispatcher !== dispatcher) {
      setDraggedDriver(null);
      setDropTarget(null);
      return;
    }

    const group = filteredData.find(g => g.dispatcher === dispatcher);
    if (!group) return;

    const currentOrder = group.drivers.map(d => d.driver);
    const draggedIndex = currentOrder.indexOf(draggedDriver.id);
    let targetIndex = currentOrder.indexOf(targetDriverId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Remove dragged item
    currentOrder.splice(draggedIndex, 1);

    // Adjust target index if needed (because we removed an item)
    if (draggedIndex < targetIndex) {
      targetIndex--;
    }

    // Insert at target position (after if dropping on lower half)
    const insertIndex = dropTarget?.position === 'after' ? targetIndex + 1 : targetIndex;
    currentOrder.splice(insertIndex, 0, draggedDriver.id);

    // Update state
    const newSortOrder = {
      ...driverSortOrder,
      [dispatcher]: currentOrder
    };
    setDriverSortOrder(newSortOrder);

    // Persist
    try {
      await userSettingsService.saveUserSetting('drivers_sort_order', newSortOrder);
    } catch (error) {
      console.error('Failed to save driver sort order:', error);
    }

    setDraggedDriver(null);
    setDropTarget(null);
  }, [draggedDriver, dropTarget, filteredData, driverSortOrder]);

  // Reset driver sort order to default
  const handleResetSortOrder = async () => {
    if (!window.confirm('Reset driver order to default? This will remove your custom sorting.')) {
      return;
    }

    setDriverSortOrder({});

    try {
      await userSettingsService.deleteUserSetting('drivers_sort_order');
    } catch (error) {
      console.error('Failed to reset driver sort order:', error);
    }
  };

  const toggleDispatcher = (dispatcher: string) => {
    setExpandedDispatchers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dispatcher)) {
        newSet.delete(dispatcher);
      } else {
        newSet.add(dispatcher);
      }
      return newSet;
    });
  };

  // Open modal for Unknown status
  const openStatusModal = (driver: string, driverName: string, dateKey: string, region: string, statusInfo?: StatusInfo) => {
    setModalDriver(driver);
    setModalDriverName(driverName);
    setModalDate(dateKey);
    setModalRegion(region);

    // Check if there's an existing status entry (WORKING or NOT WORKING)
    const hasExistingStatus = statusInfo?.status === 'Available' || statusInfo?.status === 'WORKING' ||
                              statusInfo?.status === 'Not Working' || statusInfo?.status === 'NOT WORKING';
    setModalHasExistingStatus(hasExistingStatus);

    // Pre-populate with existing values if available
    if (statusInfo?.status === 'Available' || statusInfo?.status === 'WORKING') {
      setModalWorking('WORKING');
      setModalWorkingCategory(statusInfo?.workingcategory || '');
      setModalConfirmed(statusInfo?.confirmed === true); // Default to false if undefined
    } else if (statusInfo?.status === 'Not Working' || statusInfo?.status === 'NOT WORKING') {
      setModalWorking('NOT_WORKING');
      setModalWorkingCategory('');
      setModalConfirmed(false);
    } else {
      // Default for Unknown status
      setModalWorking('UNKNOWN');
      setModalWorkingCategory('');
      setModalConfirmed(false);
    }

    setModalComment(statusInfo?.comments?.trim() || '');
    setModalChangedBy(statusInfo?.changed_by || '');
    setModalChangedDateTime(statusInfo?.changed_datetime || '');
    setModalOpen(true);
  };

  // Open the personal-notes modal for a driver (private note, only this user sees it)
  const openNotesModal = (driver: string, driverName: string) => {
    setNotesModalDriver(driver);
    setNotesModalDriverName(driverName);
    setNotesModalText(driverNotes[driver] || '');
    setNotesModalOpen(true);
  };

  // Save (upsert) the personal note. An empty note clears it.
  const saveNote = async () => {
    const driver = notesModalDriver;
    const text = notesModalText.trim();
    setNotesSaving(true);
    try {
      await api.put(`/safety/driver-notes/${encodeURIComponent(driver)}`, { note_text: text });
      setDriverNotes(prev => {
        const next = { ...prev };
        if (text) {
          next[driver] = text;
        } else {
          delete next[driver];
        }
        return next;
      });
      setNotesModalOpen(false);
    } catch (error) {
      console.error('Error saving driver note:', error);
    } finally {
      setNotesSaving(false);
    }
  };

  // Delete the personal note for the current driver
  const deleteNote = async () => {
    const driver = notesModalDriver;
    setNotesSaving(true);
    try {
      await api.delete(`/safety/driver-notes/${encodeURIComponent(driver)}`);
      setDriverNotes(prev => {
        const next = { ...prev };
        delete next[driver];
        return next;
      });
      setNotesModalOpen(false);
    } catch (error) {
      console.error('Error deleting driver note:', error);
    } finally {
      setNotesSaving(false);
    }
  };

  // Save status from modal
  const saveStatus = async () => {
    // If Unknown is selected and no comment, clear the status
    // If Unknown but has a comment, save the record with empty status to persist the note
    if (modalWorking === 'UNKNOWN' && !modalComment.trim()) {
      await clearStatus(modalDriver, modalDate);
      return;
    }

    setSaving(true);
    try {
      await api.post('/safety/status/update', {
        driver: modalDriver,
        status_date: modalDate,
        status: modalWorking === 'UNKNOWN' ? null : (modalWorking === 'WORKING' ? 'WORKING' : 'NOT WORKING'),
        workingcategory: modalWorking === 'UNKNOWN' ? null : (modalWorkingCategory || null),
        comments: modalComment.trim() || ' ',
        confirmed: modalWorking === 'UNKNOWN' ? false : modalConfirmed
      });

      // Refresh data (invalidate any in-flight auto-refresh)
      const currentFetchId = ++fetchIdRef.current;
      const startDate = formatDateKey(weekStart);
      const endDate = formatDateKey(weekDays[6]);
      const response = await api.get('/safety/status/weekly', {
        params: { start_date: startDate, end_date: endDate }
      });
      if (currentFetchId === fetchIdRef.current) {
        setData(response.data || []);
      }

      setModalOpen(false);
    } catch (error) {
      console.error('Error saving driver status:', error);
    } finally {
      setSaving(false);
    }
  };

  // Clear status (set to unknown) - works for both modal and drawer
  const clearStatus = async (driver: string, date: string) => {
    setSaving(true);
    try {
      await api.post('/safety/status/update', {
        driver: driver,
        status_date: date,
        status: null,  // Clear the status
        workingcategory: null,
        comments: null,
        confirmed: false
      });

      // Refresh data (invalidate any in-flight auto-refresh)
      const currentFetchId = ++fetchIdRef.current;
      const startDate = formatDateKey(weekStart);
      const endDate = formatDateKey(weekDays[6]);
      const response = await api.get('/safety/status/weekly', {
        params: { start_date: startDate, end_date: endDate }
      });
      if (currentFetchId === fetchIdRef.current) {
        setData(response.data || []);
      }

      setModalOpen(false);
      setDrawerOpen(false);
    } catch (error) {
      console.error('Error clearing driver status:', error);
    } finally {
      setSaving(false);
    }
  };

  // Save status from drawer status selector
  const saveDrawerStatus = async (status: string | null, workingCategory: string | null, comment: string, confirmed: boolean) => {
    await api.post('/safety/status/update', {
      driver: drawerDriver,
      status_date: drawerDate,
      status: status,
      workingcategory: workingCategory,
      comments: comment,
      confirmed: confirmed
    });

    // Refresh data
    const currentFetchId = ++fetchIdRef.current;
    const startDate = formatDateKey(weekStart);
    const endDate = formatDateKey(weekDays[6]);
    const response = await api.get('/safety/status/weekly', {
      params: { start_date: startDate, end_date: endDate }
    });
    if (currentFetchId === fetchIdRef.current) {
      setData(response.data || []);
    }

    setDrawerOpen(false);
    setDrawerLegs([]);
  };

  // Open parking location modal
  const openParkingModal = (driver: DriverStatus) => {
    setParkingModalDriver(driver.driver);
    setParkingModalDriverName(driver.driver_name);
    setParkingModalCurrentLoc(driver.parkingloc);
    setParkingModalCurrentName(driver.parking_location_name);
    setParkingModalCurrentColor(driver.parking_color);
    setParkingModalOpen(true);
  };

  // Handle parking location save - refresh data
  const handleParkingLocationSave = async () => {
    const currentFetchId = ++fetchIdRef.current;
    const startDate = formatDateKey(weekStart);
    const endDate = formatDateKey(weekDays[6]);
    const response = await api.get('/safety/status/weekly', {
      params: { start_date: startDate, end_date: endDate }
    });
    if (currentFetchId === fetchIdRef.current) {
      setData(response.data || []);
    }
  };

  // Open drawer to show driver legs
  const openLegsDrawer = async (driver: string, driverName: string, dateKey: string, confirmed?: boolean, changedBy?: string, changedDateTime?: string, hasExistingStatus?: boolean, comment?: string, status?: string | null, workingCategory?: string | null) => {
    setDrawerDriver(driver);
    setDrawerDriverName(driverName);
    setDrawerDate(dateKey);
    setDrawerConfirmed(confirmed ?? false);
    setDrawerChangedBy(changedBy || '');
    setDrawerChangedDateTime(changedDateTime || '');
    setDrawerHasExistingStatus(hasExistingStatus ?? false);
    setDrawerComment(comment || '');
    setDrawerStatus(status ?? null);
    setDrawerWorkingCategory(workingCategory ?? null);
    setDrawerOpen(true);
    setLoadingLegs(true);
    setDrawerLegs([]);

    try {
      const response = await api.get(`/driver-dispatch/driver-current-dispatches/${driver}`, {
        params: { date: dateKey }
      });
      // Map the response to match ProDetailsDrawer's expected format
      const dispatches = response.data?.dispatches || [];
      const mappedLegs = dispatches.map((d: any) => ({
        pronumber: d.pronumber,
        legrecno: d.legrecno,
        legnum: d.legnum,
        legtype: d.legtype,
        legaction2: d.legaction2,
        legstatus: d.legstatus,
        isComplete: d.isComplete,
        legdrop: d.legdrop,
        origcode: d.origcode,
        destcode: d.destcode,
        orig_name: d.originName,
        orig_address: d.origAddress,
        orig_city_state: d.origCityState,
        dest_name: d.destName,
        dest_address: d.destAddress,
        dest_city_state: d.destCityState,
        origapptdate: d.origApptDate,
        origappttime1: d.origApptTime1,
        origappttime2: d.origApptTime2,
        destapptdate: d.destApptDate,
        destappttime1: d.destApptTime1,
        destappttime2: d.destApptTime2,
        dispatchstatus: d.sendtogwimove === 'N' ? 'Not Sent' : d.acceptreject === 'A' ? 'Accepted' : 'Not Accepted',
        legmiles: d.legmiles,
        estimateddriverpay: d.estimateddriverpay,
        pregatepin: d.pregatepin,
        emptypregatepin: d.emptypregatepin,
        pregatechassis: d.pregatechassis,
        emptypregatechassis: d.emptypregatechassis,
      }));
      setDrawerLegs(mappedLegs);
    } catch (error) {
      console.error('Error fetching driver legs:', error);
    } finally {
      setLoadingLegs(false);
    }
  };

  // Open order details drawer (from PRO click in ProDetailsDrawer)
  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Bulk edit functions
  const toggleCellSelection = (driver: string, dateKey: string) => {
    const cellKey = `${driver}_${dateKey}`;
    setSelectedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cellKey)) {
        newSet.delete(cellKey);
      } else {
        newSet.add(cellKey);
      }
      return newSet;
    });
  };

  const addCellToSelection = (driver: string, dateKey: string) => {
    const cellKey = `${driver}_${dateKey}`;
    setSelectedCells(prev => {
      const newSet = new Set(prev);
      newSet.add(cellKey);
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedCells(new Set());
  };

  const exitBulkMode = () => {
    setBulkEditMode(false);
    clearSelection();
    setIsDragging(false);
    setDragStartCell(null);
  };

  const handleCellMouseDown = (e: React.MouseEvent, driver: string, dateKey: string, isSelectable: boolean) => {
    if (!bulkEditMode || !isSelectable) return;

    e.preventDefault(); // Prevent text selection
    setIsDragging(true);
    setDragStartCell(`${driver}_${dateKey}`);
    toggleCellSelection(driver, dateKey);
  };

  const handleCellMouseEnter = (driver: string, dateKey: string, isSelectable: boolean) => {
    if (!bulkEditMode || !isDragging || !isSelectable) return;

    addCellToSelection(driver, dateKey);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStartCell(null);
  };

  const openBulkModal = () => {
    setBulkStatus('UNKNOWN');
    setBulkWorkingCategory('');
    setBulkComment('');
    setBulkModalOpen(true);
  };

  // Get unique drivers and dates from selected cells
  const getSelectedCellsInfo = () => {
    const drivers = new Set<string>();
    const dates = new Set<string>();
    const cells: Array<{ driver: string; date: string; driverName: string }> = [];

    selectedCells.forEach(cellKey => {
      const [driver, date] = cellKey.split('_');
      drivers.add(driver);
      dates.add(date);

      // Find driver name
      let driverName = '';
      for (const group of data) {
        const driverData = group.drivers.find(d => d.driver === driver);
        if (driverData) {
          driverName = driverData.driver_name;
          break;
        }
      }
      cells.push({ driver, date, driverName });
    });

    return { drivers, dates, cells, count: selectedCells.size };
  };

  const saveBulkStatus = async () => {
    setBulkSaving(true);
    try {
      const { cells } = getSelectedCellsInfo();

      // Update each selected cell
      // For UNKNOWN, send null to clear the status (same as single edit)
      const statusMap: Record<string, string | null> = {
        'NOT_WORKING': 'NOT WORKING',
        'UNKNOWN': null,
        'WORKING': 'WORKING'
      };
      const updates = cells.map(cell =>
        api.post('/safety/status/update', {
          driver: cell.driver,
          status_date: cell.date,
          status: statusMap[bulkStatus],
          workingcategory: bulkStatus === 'UNKNOWN' ? null : (bulkWorkingCategory || null),
          comments: bulkStatus === 'UNKNOWN' ? (bulkComment.trim() || null) : (bulkComment.trim() || ' ')
        })
      );

      await Promise.all(updates);

      // Refresh data (invalidate any in-flight auto-refresh)
      const currentFetchId = ++fetchIdRef.current;
      const startDate = formatDateKey(weekStart);
      const endDate = formatDateKey(weekDays[6]);
      const response = await api.get('/safety/status/weekly', {
        params: { start_date: startDate, end_date: endDate }
      });
      if (currentFetchId === fetchIdRef.current) {
        setData(response.data || []);
      }

      setBulkModalOpen(false);
      exitBulkMode();
    } catch (error) {
      console.error('Error saving bulk driver status:', error);
    } finally {
      setBulkSaving(false);
    }
  };

  // Keyboard shortcuts for tab switching (Alt/Option + 1-5)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Alt/Option key is pressed along with a number 1-5
      // Use e.code to handle Mac's Alt key which produces special characters
      if (e.altKey) {
        const codeMatch = e.code.match(/^Digit([1-6])$/);
        if (codeMatch) {
          const tabIndex = parseInt(codeMatch[1]) - 1;
          const tabs: ('working' | 'preferences' | 'planning' | 'planning-week' | 'grounded-yard' | 'declined-moves')[] = ['working', 'preferences', 'planning', 'planning-week', 'grounded-yard', 'declined-moves'];
          if (tabIndex < tabs.length) {
            e.preventDefault();
            setActiveTab(tabs[tabIndex]);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Global mouseup handler for drag selection
  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Refresh data when tab changes
  useEffect(() => {
    setRefreshTrigger(prev => prev + 1);
  }, [activeTab]);

  // Background refresh timer (30 seconds) - only for active tab
  useVisibilityAwareInterval(() => setRefreshTrigger(prev => prev + 1), 30000);


  // Fetch data when week changes or refresh triggered (only for working tab)
  useEffect(() => {
    if (activeTab !== 'working') return;

    const fetchData = async () => {
      // Only show loading on initial load, not on background refresh
      const isBackgroundRefresh = initialLoadCompleteRef.current && activeTab === 'working';
      const currentFetchId = ++fetchIdRef.current;

      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      try {
        const startDate = formatDateKey(weekStart);
        const endDate = formatDateKey(weekDays[6]);

        const response = await api.get('/safety/status/weekly', {
          params: { start_date: startDate, end_date: endDate }
        });

        // Only update if this is still the latest fetch (prevents stale auto-refresh from overwriting fresh data)
        if (currentFetchId !== fetchIdRef.current) return;

        setData(response.data || []);
        if (!initialLoadCompleteRef.current) {
          // Expand all dispatchers on initial load
          setExpandedDispatchers(new Set((response.data || []).map((g: DispatcherGroup) => g.dispatcher)));
          initialLoadCompleteRef.current = true;
        }
      } catch (error) {
        console.error('Error fetching driver status:', error);
        if (currentFetchId !== fetchIdRef.current) return;
        if (!isBackgroundRefresh) {
          setData([]);
        }
      } finally {
        if (currentFetchId === fetchIdRef.current && !isBackgroundRefresh) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [weekStart, weekDays, refreshTrigger, activeTab]);

  // Fetch this user's personal driver notes (private to them). Loaded alongside
  // the working tab and refreshed on the same trigger so the notes icon stays in sync.
  useEffect(() => {
    if (activeTab !== 'working') return;

    let cancelled = false;
    const fetchNotes = async () => {
      try {
        const response = await api.get('/safety/driver-notes');
        if (cancelled) return;
        const map: Record<string, string> = {};
        (response.data || []).forEach((n: { driver: string; note_text: string }) => {
          if (n.note_text?.trim()) map[n.driver] = n.note_text;
        });
        setDriverNotes(map);
      } catch (error) {
        console.error('Error fetching driver notes:', error);
      }
    };

    fetchNotes();
    return () => { cancelled = true; };
  }, [refreshTrigger, activeTab]);

  // Update footer with bulk edit actions
  useEffect(() => {
    if (bulkEditMode && selectedCells.size > 0) {
      const info = getSelectedCellsInfo();

      // Set left side content (selection info)
      setGlobalFilters(
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">
            {selectedCells.size} cell{selectedCells.size !== 1 ? 's' : ''} selected
          </span>
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {info.drivers.size} driver{info.drivers.size !== 1 ? 's' : ''} × {info.dates.size} day{info.dates.size !== 1 ? 's' : ''}
          </span>
        </div>
      );

      // Set right side content (action buttons)
      setDispatchButton(
        <div className="flex items-center gap-2">
          <button
            onClick={clearSelection}
            className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Clear Selection
          </button>
          <button
            onClick={() => openBulkModal()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
          >
            Set Status
          </button>
        </div>
      );
    } else {
      // Clear footer when not in bulk mode or no selection
      setGlobalFilters(null);
      setDispatchButton(null);
    }

    // Cleanup on unmount
    return () => {
      setGlobalFilters(null);
      setDispatchButton(null);
    };
  }, [bulkEditMode, selectedCells.size, setGlobalFilters, setDispatchButton]);

  return (
    <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''}`}>
      {/* Tab Bar */}
      <div className={`${styles.tabBar} sticky top-0 z-50 bg-white dark:bg-gray-800 shadow-sm`} style={{ position: 'relative' }}>
        <GridLabel areaName="tabBar" position="top-right" />
        <div className="flex items-center">
          <div className="flex-1">
            <div className="">
              {/* Mobile Tab Selector */}
              <div className="grid grid-cols-1 sm:hidden">
                <select
                  aria-label="Select a tab"
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value as typeof activeTab)}
                  className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-panel py-2 pr-8 pl-3 text-base text-heading outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                >
                  <option value="working">Working/Not Working</option>
                  <option value="preferences">Preferences/Bans</option>
                  <option value="planning">Planning</option>
                  <option value="planning-week">Planning - Week View</option>
                  <option value="grounded-yard">Grounded Yard</option>
                  <option value="declined-moves">Declined Moves</option>
                </select>
                <svg
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  data-slot="icon"
                  aria-hidden="true"
                  className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
                >
                  <path d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" fillRule="evenodd" />
                </svg>
              </div>
              {/* Desktop Tab Navigation */}
              <div className="hidden sm:block">
                <nav aria-label="Tabs" className="isolate flex px-2 gap-1 bg-panel border-b border-gray-300 dark:border-gray-600">
                  <div className="relative flex items-center">
                    <button
                      onClick={() => setActiveTab('working')}
                      aria-current={activeTab === 'working' ? 'page' : undefined}
                      className={`group relative min-w-0 overflow-hidden text-center text-sm font-medium px-3 py-1.5 rounded-t-md mt-[3px] border-l border-t border-r focus:z-10 ${
                        activeTab === 'working'
                          ? 'border-gray-300 dark:border-gray-600 text-heading bg-gray-100 dark:bg-gray-700'
                          : 'border-gray-300/50 dark:border-gray-600/50 bg-gray-100/50 dark:bg-gray-700/50 text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/75 dark:hover:bg-gray-700/75'
                      }`}
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className={activeTab === 'working' ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                            <path d="M19,24H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5v14c0,2.757-2.243,5-5,5ZM5,2c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5c0-1.654-1.346-3-3-3H5Z" />
                            <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor">1</text>
                          </svg>
                        </span>
                        <span>Working/Not Working</span>
                      </span>
                    </button>
                  </div>
                  <div className="relative flex items-center">
                    <button
                      onClick={() => setActiveTab('preferences')}
                      className={`group relative min-w-0 overflow-hidden text-center text-sm font-medium px-3 py-1.5 rounded-t-md mt-[3px] border-l border-t border-r focus:z-10 ${
                        activeTab === 'preferences'
                          ? 'border-gray-300 dark:border-gray-600 text-heading bg-gray-100 dark:bg-gray-700'
                          : 'border-gray-300/50 dark:border-gray-600/50 bg-gray-100/50 dark:bg-gray-700/50 text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/75 dark:hover:bg-gray-700/75'
                      }`}
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className={activeTab === 'preferences' ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                            <path d="M19,24H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5v14c0,2.757-2.243,5-5,5ZM5,2c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5c0-1.654-1.346-3-3-3H5Z" />
                            <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor">2</text>
                          </svg>
                        </span>
                        <span>Preferences/Bans</span>
                      </span>
                    </button>
                  </div>
                  <div className="relative flex items-center">
                    <button
                      onClick={() => setActiveTab('planning')}
                      className={`group relative min-w-0 overflow-hidden text-center text-sm font-medium px-3 py-1.5 rounded-t-md mt-[3px] border-l border-t border-r focus:z-10 ${
                        activeTab === 'planning'
                          ? 'border-gray-300 dark:border-gray-600 text-heading bg-gray-100 dark:bg-gray-700'
                          : 'border-gray-300/50 dark:border-gray-600/50 bg-gray-100/50 dark:bg-gray-700/50 text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/75 dark:hover:bg-gray-700/75'
                      }`}
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className={activeTab === 'planning' ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                            <path d="M19,24H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5v14c0,2.757-2.243,5-5,5ZM5,2c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5c0-1.654-1.346-3-3-3H5Z" />
                            <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor">3</text>
                          </svg>
                        </span>
                        <span>Planning</span>
                      </span>
                    </button>
                  </div>
                  <div className="relative flex items-center">
                    <button
                      onClick={() => setActiveTab('planning-week')}
                      className={`group relative min-w-0 overflow-hidden text-center text-sm font-medium px-3 py-1.5 rounded-t-md mt-[3px] border-l border-t border-r focus:z-10 ${
                        activeTab === 'planning-week'
                          ? 'border-gray-300 dark:border-gray-600 text-heading bg-gray-100 dark:bg-gray-700'
                          : 'border-gray-300/50 dark:border-gray-600/50 bg-gray-100/50 dark:bg-gray-700/50 text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/75 dark:hover:bg-gray-700/75'
                      }`}
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className={activeTab === 'planning-week' ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                            <path d="M19,24H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5v14c0,2.757-2.243,5-5,5ZM5,2c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5c0-1.654-1.346-3-3-3H5Z" />
                            <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor">4</text>
                          </svg>
                        </span>
                        <span>Planning - Week View</span>
                      </span>
                    </button>
                  </div>
                  <div className="relative flex items-center">
                    <button
                      onClick={() => setActiveTab('grounded-yard')}
                      className={`group relative min-w-0 overflow-hidden text-center text-sm font-medium px-3 py-1.5 rounded-t-md mt-[3px] border-l border-t border-r focus:z-10 ${
                        activeTab === 'grounded-yard'
                          ? 'border-gray-300 dark:border-gray-600 text-heading bg-gray-100 dark:bg-gray-700'
                          : 'border-gray-300/50 dark:border-gray-600/50 bg-gray-100/50 dark:bg-gray-700/50 text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/75 dark:hover:bg-gray-700/75'
                      }`}
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className={activeTab === 'grounded-yard' ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                            <path d="M19,24H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5v14c0,2.757-2.243,5-5,5ZM5,2c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5c0-1.654-1.346-3-3-3H5Z" />
                            <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor">5</text>
                          </svg>
                        </span>
                        <span>Grounded Yard</span>
                      </span>
                    </button>
                  </div>
                  <div className="relative flex items-center">
                    <button
                      onClick={() => setActiveTab('declined-moves')}
                      className={`group relative min-w-0 overflow-hidden text-center text-sm font-medium px-3 py-1.5 rounded-t-md mt-[3px] border-l border-t border-r focus:z-10 ${
                        activeTab === 'declined-moves'
                          ? 'border-gray-300 dark:border-gray-600 text-heading bg-gray-100 dark:bg-gray-700'
                          : 'border-gray-300/50 dark:border-gray-600/50 bg-gray-100/50 dark:bg-gray-700/50 text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/75 dark:hover:bg-gray-700/75'
                      }`}
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className={activeTab === 'declined-moves' ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                            <path d="M19,24H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5v14c0,2.757-2.243,5-5,5ZM5,2c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5c0-1.654-1.346-3-3-3H5Z" />
                            <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor">6</text>
                          </svg>
                        </span>
                        <span>Declined Moves</span>
                      </span>
                    </button>
                  </div>
                </nav>
              </div>
            </div>
          </div>
          {/* Control Buttons */}
          <div className="flex items-center gap-1 text-xs px-4 py-1 bg-panel border-b border-gray-300 dark:border-gray-600 self-stretch">
            {activeTab === 'working' && (
              <>
                <button
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    bulkEditMode
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-200 dark:bg-gray-700 text-label hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  title={bulkEditMode ? 'Exit Bulk Edit Mode' : 'Enter Bulk Edit Mode'}
                  onClick={() => bulkEditMode ? exitBulkMode() : setBulkEditMode(true)}
                >
                  {bulkEditMode ? 'Exit Bulk Edit' : 'Bulk Edit'}
                </button>
                <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
              </>
            )}
            <button
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Collapse All"
              onClick={() => setExpandedDispatchers(new Set())}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
              </svg>
            </button>
            <button
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Expand All"
              onClick={() => setExpandedDispatchers(new Set(data.map(g => g.dispatcher)))}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar label="Filters:" className={styles.filterBar}>
        <GridLabel areaName="filterBar" position="top-right" />
        {activeTab === 'planning' && (
          <div className="relative min-w-[180px]">
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={planningDate}
              onChange={(value) => setPlanningDate(value as Date | null)}
              placeholder="Select Date"
              labelType="none"
              autoApply={true}
              openDirection="down"
              usePortal={true}
              compact={true}
            />
          </div>
        )}
        {activeTab === 'grounded-yard' && (
          <div className="relative min-w-[180px]">
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={groundedYardDate}
              onChange={(value) => setGroundedYardDate(value as Date | null)}
              placeholder="Select Date"
              labelType="none"
              autoApply={true}
              openDirection="down"
              usePortal={true}
              compact={true}
            />
          </div>
        )}
        {activeTab === 'declined-moves' && (
          <div className="relative min-w-[180px]">
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={declinedMovesDate}
              onChange={(value) => setDeclinedMovesDate(value as Date | null)}
              placeholder="Select Date"
              labelType="none"
              autoApply={true}
              openDirection="down"
              usePortal={true}
              compact={true}
            />
          </div>
        )}
        <DriverDispatcherFilter
          value={selectedDispatchers}
          onChange={setSelectedDispatchers}
          className="min-w-[180px]"
        />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          enableSearch={true}
          searchPlaceholder="Search regions..."
          size="sm"
          className="min-w-[180px]"
        />
        {(activeTab === 'working' || activeTab === 'planning' || activeTab === 'planning-week') && (
          <MultiSelect
            options={driverTypeOptions}
            value={selectedDriverType}
            onChange={setSelectedDriverType}
            placeholder="Preference"
            showClearOption={true}
            clearOptionLabel="All"
            size="sm"
            className="min-w-[100px]"
          />
        )}
        {activeTab === 'planning-week' && (
          <input
            type="text"
            value={deliveryLocationQuery}
            onChange={(e) => setDeliveryLocationQuery(e.target.value)}
            placeholder="Delivery location"
            title="Show only drivers with a move at a matching location this week"
            aria-label="Filter by delivery location"
            className="min-w-[180px] py-1 px-2 text-xs bg-white border border-gray-400 rounded-lg text-heading placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:placeholder-gray-500 dark:focus:ring-gray-600"
          />
        )}
        {(activeTab === 'planning' || activeTab === 'planning-week') && (
          <>
            <FilterBar.Spacer />
            <div className="flex items-center gap-4">
              {activeTab === 'planning-week' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
                  <Toggle
                    checked={showStatus}
                    onChange={setShowStatus}
                    size="sm"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Appt Times</span>
                <Toggle
                  checked={showAppointmentTimes}
                  onChange={setShowAppointmentTimes}
                  size="sm"
                />
              </div>
              {activeTab === 'planning' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">TWIC</span>
                  <Toggle
                    checked={planningShowTwic}
                    onChange={setPlanningShowTwic}
                    size="sm"
                  />
                </div>
              )}
              {activeTab === 'planning-week' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Quals</span>
                  <Toggle
                    checked={showQualifications}
                    onChange={setShowQualifications}
                    size="sm"
                  />
                </div>
              )}
              <FilterBar.Divider />
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Group By Dispatcher</span>
                <Toggle
                  checked={groupByDispatcher}
                  onChange={setGroupByDispatcher}
                  size="sm"
                />
              </div>
              {activeTab === 'planning-week' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Group By Local/OTR</span>
                  <Toggle
                    checked={groupByPreference}
                    onChange={setGroupByPreference}
                    size="sm"
                  />
                </div>
              )}
            </div>
          </>
        )}
        {activeTab === 'grounded-yard' && (
          <>
            <FilterBar.Spacer />
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Group By Dispatcher</span>
                <Toggle
                  checked={groupByDispatcher}
                  onChange={setGroupByDispatcher}
                  size="sm"
                />
              </div>
            </div>
          </>
        )}
        {activeTab === 'declined-moves' && (
          <>
            <FilterBar.Spacer />
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Group By Dispatcher</span>
                <Toggle
                  checked={groupByDispatcher}
                  onChange={setGroupByDispatcher}
                  size="sm"
                />
              </div>
            </div>
          </>
        )}
        {/* Font Size Control - always visible, right-aligned */}
        {!(activeTab === 'planning' || activeTab === 'planning-week' || activeTab === 'grounded-yard' || activeTab === 'declined-moves') && (
          <FilterBar.Spacer />
        )}
        {activeTab === 'working' && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Quals</span>
              <Toggle
                checked={showWorkingQuals}
                onChange={setShowWorkingQuals}
                size="sm"
              />
            </div>
            <FilterBar.Divider />
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Group</span>
              <Toggle
                checked={groupByDispatcher}
                onChange={setGroupByDispatcher}
                size="sm"
              />
            </div>
          </>
        )}
        <FilterBar.Divider />
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-muted whitespace-nowrap">Font Size:</span>
          <button
            type="button"
            onClick={() => handleZoomChange(pageZoom - 5)}
            disabled={pageZoom <= 70}
            className="w-5 h-5 flex items-center justify-center rounded text-xs font-bold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Decrease font size"
          >
            −
          </button>
          <span className="text-xs text-gray-600 dark:text-gray-300 w-8 text-center tabular-nums">{pageZoom}%</span>
          <button
            type="button"
            onClick={() => handleZoomChange(pageZoom + 5)}
            disabled={pageZoom >= 130}
            className="w-5 h-5 flex items-center justify-center rounded text-xs font-bold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Increase font size"
          >
            +
          </button>
        </div>
      </FilterBar>

      {/* Body */}
      <div className={`${styles.body} p-4 flex flex-col`} style={{ position: 'relative', zoom: pageZoom !== 100 ? pageZoom / 100 : undefined }}>
        <GridLabel areaName="body" position="top-right" />
        {activeTab === 'planning' ? (
          <DriverPlanning
            selectedDispatchers={selectedDispatchers}
            selectedRegions={selectedRegions}
            selectedDriverType={selectedDriverType}
            selectedDate={planningDate}
            refreshTrigger={refreshTrigger}
            groupByDispatcher={groupByDispatcher}
            showAppointmentTimes={showAppointmentTimes}
            usernameToDisplayname={usernameToDisplayname}
            onOpenLegsDrawer={openLegsDrawer}
          />
        ) : activeTab === 'planning-week' ? (
          <DriverPlanningWeek
            selectedDispatchers={selectedDispatchers}
            selectedRegions={selectedRegions}
            selectedDriverType={selectedDriverType}
            deliveryLocationQuery={deliveryLocationQuery}
            weekStart={weekStart}
            refreshTrigger={refreshTrigger}
            groupByDispatcher={groupByDispatcher}
            groupByPreference={groupByPreference}
            showStatus={showStatus}
            showAppointmentTimes={showAppointmentTimes}
            showQualifications={showQualifications}
            onOpenLegsDrawer={openLegsDrawer}
            weekOptions={weekOptions}
            canGoBack={canGoBack}
            canGoForward={canGoForward}
            onWeekChange={handleWeekSelect}
            onNavigateWeek={navigateWeek}
            usernameToDisplayname={usernameToDisplayname}
          />
        ) : activeTab === 'grounded-yard' ? (
          <DriverGroundedYard
            selectedDispatchers={selectedDispatchers}
            selectedRegions={selectedRegions}
            selectedDate={groundedYardDate}
            refreshTrigger={refreshTrigger}
            groupByDispatcher={groupByDispatcher}
            usernameToDisplayname={usernameToDisplayname}
          />
        ) : activeTab === 'working' ? (
          (() => {
            // Distribute remaining width evenly across uncollapsed day columns.
            // Fixed columns: drag handle (20px) + label (450px) + quals (130px, optional) = 470–600px.
            const qualsColWidth = showWorkingQuals ? WORKING_QUALS_COL_WIDTH : 0;
            const fixedWidth = 470 + qualsColWidth;
            const expandedCount = 7 - collapsedDays.size;
            const collapsedWidth = collapsedDays.size * 48;
            const expandedColumnWidth = expandedCount > 0
              ? `calc((100% - ${fixedWidth}px - ${collapsedWidth}px) / ${expandedCount})`
              : 'auto';
            return (
          <div className="flex flex-col flex-1 min-h-0">
            {/* Stats Section */}
            <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 px-6 py-2 flex-wrap -mx-4 -mt-4 mb-2">
              <span className="text-sm font-medium text-label whitespace-nowrap">Stats:</span>
              <div className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300">
                Total: <span className="font-bold">{workingStats.total}</span>
              </div>
              <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
              {DRIVER_QUALIFICATIONS.map(q => {
                const isActive = qualificationsFilter.includes(q);
                const { base, border } = getQualStatStyle(q);
                return (
                  <button
                    key={q}
                    onClick={() => setQualificationsFilter(prev =>
                      prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]
                    )}
                    className={`px-2 py-0.5 rounded text-xs font-medium border-2 transition-colors cursor-pointer ${base} ${isActive ? border : 'border-transparent'}`}
                    title={isActive ? `Remove ${q} filter` : `Filter by ${q}`}
                  >
                    {q}: <span className="font-bold">{workingStats.qualCounts[q] ?? 0}</span>
                  </button>
                );
              })}
              <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
              {(['OTR', 'LOCAL'] as const).map(type => {
                const isActive = selectedDriverType.includes(type);
                const styles = type === 'OTR'
                  ? { base: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', border: 'border-purple-500 dark:border-purple-400' }
                  : { base: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300', border: 'border-teal-500 dark:border-teal-400' };
                const count = type === 'OTR' ? workingStats.otrCount : workingStats.localCount;
                return (
                  <button
                    key={type}
                    onClick={() => setSelectedDriverType(prev =>
                      prev.includes(type) ? prev.filter(x => x !== type) : [...prev, type]
                    )}
                    className={`px-2 py-0.5 rounded text-xs font-medium border-2 transition-colors cursor-pointer ${styles.base} ${isActive ? styles.border : 'border-transparent'}`}
                    title={isActive ? `Remove ${type} filter` : `Filter by ${type}`}
                  >
                    {type}: <span className="font-bold">{count}</span>
                  </button>
                );
              })}
            </div>
          <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto mb-16">
        <table className="w-full border-collapse table-fixed">
          {/* Header */}
          <thead className="bg-slate-600 dark:bg-slate-700 text-white sticky top-0 z-30">
            <tr>
              <th className="w-5 px-1 py-1 border-r border-slate-500"></th>
              <th className="px-2 py-1 text-center font-semibold border-r border-slate-500 whitespace-nowrap w-[450px] text-xs">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => navigateWeek(-1)}
                    disabled={!canGoBack}
                    className={`p-1 rounded ${canGoBack ? 'hover:bg-slate-500' : 'opacity-40 cursor-not-allowed'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 fill-current rotate-180">
                      <path d="M15.75,9.525,11.164,4.939A1.5,1.5,0,0,0,9.043,7.061l4.586,4.585a.5.5,0,0,1,0,.708L9.043,16.939a1.5,1.5,0,0,0,2.121,2.122l4.586-4.586A3.505,3.505,0,0,0,15.75,9.525Z"/>
                    </svg>
                  </button>
                  <select
                    value={formatDateKey(weekStart)}
                    onChange={(e) => handleWeekSelect(e.target.value)}
                    className="bg-slate-500 text-white border-none rounded px-2 py-1 text-sm font-semibold cursor-pointer hover:bg-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    {weekOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => navigateWeek(1)}
                    className="p-1 hover:bg-slate-500 rounded"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                      <path d="M15.75,9.525,11.164,4.939A1.5,1.5,0,0,0,9.043,7.061l4.586,4.585a.5.5,0,0,1,0,.708L9.043,16.939a1.5,1.5,0,0,0,2.121,2.122l4.586-4.586A3.505,3.505,0,0,0,15.75,9.525Z"/>
                    </svg>
                  </button>
                </div>
              </th>
              <th
                className="text-center font-semibold border-r border-slate-500 text-xs overflow-hidden"
                style={{ width: showWorkingQuals ? WORKING_QUALS_COL_WIDTH : 0, padding: showWorkingQuals ? undefined : 0 }}
              >
                {showWorkingQuals && (
                  <button
                    onClick={() => handleColumnSort('endorsements')}
                    className="flex items-center justify-center gap-0.5 mx-auto hover:text-white/70 whitespace-nowrap px-2 py-1"
                    title="Sort by qualifications"
                  >
                    Quals
                    {sortColumn === 'endorsements' ? (
                      sortDirection === 'asc' ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />
                    ) : (
                      <span className="text-white/40 text-[0.625rem]">↕</span>
                    )}
                  </button>
                )}
              </th>
              {weekDays.map((date, index) => {
                const { day, initial, date: dayNum } = formatDayHeader(date);
                const isToday = formatDateKey(date) === formatLocalDateKey(new Date());
                const isCollapsed = collapsedDays.has(index);
                return (
                  <th
                    key={index}
                    className={`px-1 py-1 text-center font-normal whitespace-nowrap text-xs ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px]' : ''} ${index < 6 ? 'border-r border-slate-500' : ''} ${isToday ? 'bg-slate-800' : ''}`}
                    style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                  >
                    {isCollapsed ? (
                      <button
                        onClick={() => toggleDayCollapsed(index)}
                        className="flex items-center justify-center gap-0.5 hover:bg-slate-500 rounded p-1 mx-auto"
                        title="Expand day"
                      >
                        <span className="text-sm font-bold">{initial}</span>
                        <ChevronRightIcon className="h-3 w-3" />
                      </button>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => toggleDayCollapsed(index)}
                          className="p-0.5 hover:bg-slate-500 rounded"
                          title="Collapse day"
                        >
                          <ChevronRightIcon className="h-3 w-3 rotate-180" />
                        </button>
                        <span
                          onClick={() => {
                            setPlanningDate(date);
                            setActiveTab('planning');
                          }}
                          className="cursor-pointer hover:bg-slate-700 rounded px-1 py-0.5 transition-colors"
                          title="Click to view planning for this date"
                        >
                          {isToday ? (
                            <span className="font-semibold">Today</span>
                          ) : (
                            <>{day} <span className="font-semibold">{dayNum}</span></>
                          )}
                        </span>
                        <button
                          onClick={() => handleColumnSort(`day_${index}`)}
                          className="p-0.5 hover:bg-slate-500 rounded"
                          title="Sort by this day's status"
                        >
                          {sortColumn === `day_${index}` ? (
                            sortDirection === 'asc' ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />
                          ) : (
                            <span className="text-white/40 text-[0.625rem]">↕</span>
                          )}
                        </button>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
            {/* Filter Row */}
            <tr className="bg-slate-500 relative z-30">
              <th className="w-5 px-1 py-0.5 border-r border-slate-400"></th>
              <th className="px-1 py-0.5 text-center border-r border-slate-400">
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Filter driver"
                    className="flex-1 px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                  <button
                    onClick={() => handleColumnSort('driver_name')}
                    className={`px-2 py-1 text-xs border border-slate-400 rounded whitespace-nowrap flex items-center gap-0.5 ${sortColumn === 'driver_name' ? 'bg-slate-300 text-slate-900' : 'text-slate-200 hover:text-white hover:bg-slate-400'}`}
                    title="Sort by driver name"
                  >
                    Name
                    {sortColumn === 'driver_name' ? (
                      sortDirection === 'asc' ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />
                    ) : (
                      <span className="text-white/40 text-[0.625rem]">↕</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleColumnSort('driver_number')}
                    className={`px-2 py-1 text-xs border border-slate-400 rounded whitespace-nowrap flex items-center gap-0.5 ${sortColumn === 'driver_number' ? 'bg-slate-300 text-slate-900' : 'text-slate-200 hover:text-white hover:bg-slate-400'}`}
                    title="Sort by driver number"
                  >
                    #
                    {sortColumn === 'driver_number' ? (
                      sortDirection === 'asc' ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />
                    ) : (
                      <span className="text-white/40 text-[0.625rem]">↕</span>
                    )}
                  </button>
                  {sortColumn && (
                    <button
                      onClick={() => setSortColumn(null)}
                      className="px-2 py-1 text-xs text-slate-200 hover:text-white border border-slate-400 rounded hover:bg-slate-400 whitespace-nowrap"
                      title="Clear sort, restore custom order"
                    >
                      ✕
                    </button>
                  )}
                  {!sortColumn && (
                    <button
                      onClick={handleResetSortOrder}
                      className="px-2 py-1 text-xs text-slate-200 hover:text-white border border-slate-400 rounded hover:bg-slate-400 whitespace-nowrap"
                      title="Reset driver order to default"
                    >
                      Reset Order
                    </button>
                  )}
                </div>
              </th>
              <th
                className="border-r border-slate-400 overflow-hidden"
                style={{ width: showWorkingQuals ? WORKING_QUALS_COL_WIDTH : 0, padding: showWorkingQuals ? '2px 4px' : 0 }}
              >
                {showWorkingQuals && (
                  <MultiSelect
                    options={qualificationOptions()}
                    value={qualificationsFilter}
                    onChange={setQualificationsFilter}
                    placeholder="All"
                    showClearOption={true}
                    clearOptionLabel="All"
                    enableSearch={false}
                    size="sm"
                    className="w-full"
                    usePortal={true}
                  />
                )}
              </th>
              {weekDays.map((date, index) => {
                const isToday = formatDateKey(date) === formatLocalDateKey(new Date());
                const isCollapsed = collapsedDays.has(index);
                const counts = getStatusCountsForDay(index);
                const statusOptionsWithCounts = baseStatusOptions.map(opt => ({
                  ...opt,
                  label: `${opt.label} (${counts[opt.value] || 0})`
                }));

                return (
                  <th
                    key={index}
                    className={`px-1 py-0.5 ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px]' : ''} ${index < 6 ? 'border-r border-slate-400' : ''} ${isToday ? 'bg-slate-800' : ''}`}
                    style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                  >
                    {!isCollapsed && (
                      <MultiSelect
                        options={statusOptionsWithCounts}
                        value={columnFilters[index] || []}
                        onChange={(value) => {
                          setColumnFilters(prev => ({
                            ...prev,
                            [index]: value
                          }));
                        }}
                        placeholder="All"
                        showClearOption={true}
                        clearOptionLabel="All"
                        enableSearch={false}
                        size="sm"
                        className="w-full"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-panel">
            {loading ? (
              <>
                {/* Loading skeleton - show table structure */}
                {[1, 2, 3].map((groupIndex) => (
                  <React.Fragment key={groupIndex}>
                    {/* Dispatcher skeleton row */}
                    <tr className="bg-slate-200 dark:bg-slate-700">
                      <td colSpan={9} className="p-2 border border-rule">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse"></div>
                          <div className="h-4 w-32 bg-slate-300 dark:bg-slate-600 rounded animate-pulse"></div>
                        </div>
                      </td>
                    </tr>
                    {/* Driver skeleton rows */}
                    {[1, 2, 3, 4].map((driverIndex) => (
                      <tr key={`${groupIndex}-${driverIndex}`}>
                        <td className="p-2 pl-8 border border-rule">
                          <div className="h-4 w-40 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                        </td>
                        {weekDays.map((_, dayIndex) => {
                          const isCollapsed = collapsedDays.has(dayIndex);
                          return (
                            <td
                              key={dayIndex}
                              className={`p-2 border border-gray-200 dark:border-gray-600 ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px]' : ''}`}
                              style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                            >
                              {!isCollapsed && (
                                <div className="h-4 w-16 mx-auto bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {/* Loading indicator overlay */}
                <tr>
                  <td colSpan={9} className="p-4 text-center text-muted bg-white/80 dark:bg-gray-800/80">
                    <div className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-slate-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Loading drivers...</span>
                    </div>
                  </td>
                </tr>
              </>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-8 text-center text-muted">
                  No driver status data for this week
                </td>
              </tr>
            ) : (
              filteredData.map((group) => {
                const isExpanded = groupByDispatcher ? expandedDispatchers.has(group.dispatcher) : true;
                return (
                  <React.Fragment key={group.dispatcher}>
                    {/* Dispatcher Row */}
                    {groupByDispatcher && (
                    <tr
                      className="bg-slate-200 dark:bg-slate-700 cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600"
                      onClick={() => toggleDispatcher(group.dispatcher)}
                    >
                      <td colSpan={10} className="px-2 py-0.5 font-semibold text-xs text-heading border border-rule">
                        <div className="flex items-center gap-1">
                          <ChevronRightIcon
                            className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                          />
                          <span>{group.dispatcher}</span>
                          <span className="text-gray-500 font-normal">
                            ({group.drivers.length})
                          </span>
                        </div>
                      </td>
                    </tr>
                    )}
                    {/* Driver Rows */}
                    {isExpanded && group.drivers.map((driver) => (
                      <tr
                        key={driver.driver}
                        draggable={groupByDispatcher}
                        onDragStart={groupByDispatcher ? (e) => handleDragStart(e, driver.driver, group.dispatcher) : undefined}
                        onDragEnd={groupByDispatcher ? handleDragEnd : undefined}
                        onDragOver={groupByDispatcher ? (e) => handleDragOver(e, driver.driver, group.dispatcher) : undefined}
                        onDragLeave={groupByDispatcher ? handleDragLeave : undefined}
                        onDrop={groupByDispatcher ? (e) => handleDrop(e, driver.driver, group.dispatcher) : undefined}
                        className={`
                          bg-white dark:bg-gray-800
                          ${draggedDriver?.id === driver.driver ? 'opacity-50' : ''}
                          ${groupByDispatcher && dropTarget?.id === driver.driver && dropTarget.position === 'before' ? 'border-t-2 border-t-blue-500' : ''}
                          ${groupByDispatcher && dropTarget?.id === driver.driver && dropTarget.position === 'after' ? 'border-b-2 border-b-blue-500' : ''}
                        `}
                      >
                        {/* Drag handle cell */}
                        <td
                          data-drag-handle
                          className={`w-5 px-0.5 py-0 text-center border border-gray-200 dark:border-gray-600 text-gray-400 text-xs leading-none ${groupByDispatcher ? 'cursor-grab active:cursor-grabbing hover:text-gray-600 dark:hover:text-gray-300' : 'cursor-default'}`}
                        >
                          {groupByDispatcher ? '⋮⋮' : ''}
                        </td>
                                    <td className="px-2 py-0.5 pl-3 text-gray-700 dark:text-gray-200 border border-rule">
                          <div className="text-xs flex items-center gap-1 flex-nowrap">
                            <a
                              href={`${config.V2_URL}/safety/drivers/${driver.driver}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium"
                            >
                              {driver.driver}<MuleBadge isMule={driver.isMule} />
                            </a>
                            <DriverStatusDot driverId={driver.driver} />
                            <span className="text-gray-500 dark:text-white truncate">
                              {driver.driver_name ? toTitleCase(driver.driver_name) : null}
                            </span>
                            <DriverPhoneLink textmsgnumber={driver.textmsgnumber} homephone={driver.homephone} />
                            {driver.preference && (
                              <span className={`px-1 rounded text-[0.625rem] leading-tight ${
                                driver.preference === 'OTR'
                                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                  : 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
                              }`}>
                                {driver.preference}
                              </span>
                            )}
                            {(() => {
                              const isDarkMode = document.documentElement.classList.contains('dark');
                              const colorStyles = getParkingColorStyles(driver.parking_color, isDarkMode);
                              return (
                                <button
                                  onClick={() => openParkingModal(driver)}
                                  className={`text-[0.625rem] leading-tight px-1 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                                    !colorStyles && (driver.parking_location_name
                                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                      : 'bg-gray-100 text-muted dark:bg-gray-700')
                                  }`}
                                  style={colorStyles || undefined}
                                  title={driver.parking_location_name || 'Set parking location'}
                                >
                                  {driver.parking_location_name || 'Parking'}
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                        <td
                          className="border border-rule align-top overflow-hidden"
                          style={{ width: showWorkingQuals ? WORKING_QUALS_COL_WIDTH : 0, padding: showWorkingQuals ? '2px 4px' : 0 }}
                        >
                          {showWorkingQuals && (
                            <div className="flex flex-nowrap items-center gap-px">
                              {parseQualifications(driver.endorsements).map((endorsement, idx) => (
                                <Tooltip key={idx} content={endorsement}>
                                  <TagBadge
                                    label={qualificationAbbr(endorsement)}
                                    color={getQualificationColor(endorsement)}
                                    className="whitespace-nowrap text-[0.625rem] leading-none !px-1 !py-0.5 justify-center"
                                  />
                                </Tooltip>
                              ))}
                            </div>
                          )}
                        </td>
                        {weekDays.map((date, index) => {
                          const isCollapsed = collapsedDays.has(index);
                          const dateKey = formatDateKey(date);
                          const statusInfo = driver.statuses[dateKey];
                          const status = statusInfo?.status;
                          const isUnknown = status === 'Unknown';
                          const isNotWorking = status === 'Not Working';
                          const isAvailable = status === 'Available';
                          const isPlanned = status === 'Planned' || status === 'Planned Accepted';
                          const isOutOfService = status === 'Out Of Service';
                          const hasConflict = statusInfo?.has_conflict;
                          const isClickable = isUnknown || isNotWorking || isAvailable || isPlanned || (isOutOfService && hasConflict);
                          // Personal note is per-driver (private to this user); the safety/status
                          // tooltip is per-cell. The icon shows if either exists, and the tooltip
                          // combines both.
                          const personalNote = driverNotes[driver.driver];
                          const hasPersonalNote = !!personalNote?.trim();
                          const hasStatusTooltip = statusInfo?.comments?.trim() ||
                                            (isOutOfService && (statusInfo?.oos_comments?.trim() || statusInfo?.oos_type?.trim()));
                          const hasTooltip = hasStatusTooltip || hasPersonalNote;

                          // Build display content - bold for Planned Accepted, date for Pending Return
                          let displayContent;
                          const workingCategory = statusInfo?.workingcategory;

                          if (status === 'Planned Accepted') {
                            if (workingCategory) {
                              displayContent = (
                                <div className="flex flex-col">
                                  <div className="font-bold">Accepted</div>
                                  <div className="text-xs">{getWorkingCategoryLabel(workingCategory)}</div>
                                </div>
                              );
                            } else {
                              displayContent = <span className="font-bold">Accepted</span>;
                            }
                          } else if (status === 'Planned') {
                            if (workingCategory) {
                              displayContent = (
                                <div className="flex flex-col">
                                  <div>Planned</div>
                                  <div className="text-xs">{getWorkingCategoryLabel(workingCategory)}</div>
                                </div>
                              );
                            } else {
                              displayContent = 'Planned';
                            }
                          } else if (status === 'Available') {
                            if (workingCategory) {
                              displayContent = getWorkingCategoryLabel(workingCategory);
                            } else {
                              displayContent = 'Available';
                            }
                          } else if (status === 'Pending Return') {
                            displayContent = 'Out Of Service';
                          } else {
                            displayContent = getDisplayStatus(status);
                          }

                          const isOverdue = statusInfo?.is_overdue;
                          const isTodayColumn = formatDateKey(date) === formatLocalDateKey(new Date());
                          // Apply today background for Unknown status or cells without background color
                          const isTodayUnknown = isTodayColumn && (status === 'Unknown' || !getStatusStyle(status, statusInfo, driver.region));
                          const todayBg = isTodayUnknown ? 'bg-gray-100 dark:bg-gray-700' : '';

                          // Different hover effects based on cell type
                          let hoverClass = '';
                          if (isClickable) {
                            if (isTodayUnknown) {
                              hoverClass = 'hover:bg-gray-200 dark:hover:bg-gray-600';
                            } else if (isUnknown) {
                              hoverClass = 'hover:bg-gray-100 dark:hover:bg-gray-700';
                            } else {
                              hoverClass = 'hover:opacity-80';
                            }
                          }

                          const handleCellClick = () => {
                            // In bulk edit mode, selection is handled by handleCellMouseDown
                            // so we don't need to do anything here (prevents double-toggle)
                            if (bulkEditMode) {
                              return;
                            }
                            // Normal mode behavior
                            if (isPlanned || (isOutOfService && hasConflict)) {
                              const existingStatus = statusInfo?.status === 'Available' || statusInfo?.status === 'WORKING' ||
                                                     statusInfo?.status === 'Not Working' || statusInfo?.status === 'NOT WORKING';
                              openLegsDrawer(driver.driver, driver.driver_name, dateKey, statusInfo?.confirmed, statusInfo?.changed_by, statusInfo?.changed_datetime, existingStatus, statusInfo?.comments, statusInfo?.status, statusInfo?.workingcategory);
                            } else if (isUnknown || isNotWorking || isAvailable) {
                              openStatusModal(driver.driver, driver.driver_name, dateKey, driver.region, statusInfo);
                            }
                          };

                          const cellKey = `${driver.driver}_${dateKey}`;
                          const isSelected = selectedCells.has(cellKey);
                          const selectionStyle = isSelected ? 'ring-4 ring-indigo-500 ring-inset bg-indigo-50 dark:bg-indigo-900/30' : '';
                          const isSelectable = isUnknown || isNotWorking || isAvailable;

                          // Abbreviated label for collapsed columns. Status names match the
                          // values getDisplayStatus() produces; bold for Accepted to mirror the
                          // expanded-cell emphasis.
                          let collapsedAbbrev: React.ReactNode = '';
                          if (status === 'Planned Accepted') collapsedAbbrev = <span className="font-bold">AC</span>;
                          else if (status === 'Planned') collapsedAbbrev = 'P';
                          else if (status === 'Available') collapsedAbbrev = 'A';
                          else if (status === 'Not Working') collapsedAbbrev = 'NW';
                          else if (status === 'Out Of Service' || status === 'Pending Return') collapsedAbbrev = 'OOS';
                          else if (status === 'Unknown' || !status) collapsedAbbrev = 'U';

                          const tooltipContent = hasTooltip ? (
                            <div className="text-sm">
                              {hasConflict && statusInfo?.assigned_pros && statusInfo.assigned_pros.length > 0 && (
                                <div className="text-red-600 font-medium mb-1">
                                  Assigned PROs: {statusInfo.assigned_pros.join(', ')}
                                </div>
                              )}
                              {isOutOfService && statusInfo?.oos_type && (
                                <div className="font-medium mb-1">Type: {statusInfo.oos_type}</div>
                              )}
                              {statusInfo?.comments?.trim() && (
                                <div>{statusInfo.comments}</div>
                              )}
                              {isOutOfService && statusInfo?.oos_comments?.trim() && (
                                <div>{statusInfo.oos_comments}</div>
                              )}
                              {statusInfo?.changed_by && (
                                <div className={statusInfo.comments?.trim() || hasConflict ? 'mt-1 font-medium' : 'font-medium'}>Set by: {statusInfo.changed_by}</div>
                              )}
                              {hasPersonalNote && (
                                <div className={hasStatusTooltip ? 'mt-1 pt-1 border-t border-gray-300 dark:border-gray-600' : ''}>
                                  <span className="font-medium">My Note: </span>{personalNote}
                                </div>
                              )}
                            </div>
                          ) : null;

                          if (isCollapsed) {
                            const statusLabel = (() => {
                              if (status === 'Pending Return') return 'Out Of Service';
                              return getDisplayStatus(status) || 'Unknown';
                            })();
                            const collapsedTooltipContent = tooltipContent ?? (
                              <div className="text-sm font-medium">{statusLabel}</div>
                            );
                            return (
                              <td
                                key={index}
                                onClick={isClickable ? handleCellClick : undefined}
                                onMouseDown={(e) => handleCellMouseDown(e, driver.driver, dateKey, isSelectable)}
                                onMouseEnter={() => handleCellMouseEnter(driver.driver, dateKey, isSelectable)}
                                className={`w-12 min-w-[48px] max-w-[48px] px-0.5 py-0.5 text-center text-[0.625rem] leading-tight border ${hasConflict ? 'border-2 border-red-500 dark:border-red-400' : 'border-rule'} ${getStatusStyle(status, statusInfo, driver.region)} ${todayBg} ${isClickable ? `cursor-pointer ${hoverClass}` : ''} ${isOverdue ? 'italic' : ''} ${selectionStyle} select-none`}
                              >
                                <Tooltip content={collapsedTooltipContent} placement="top">
                                  <div className="relative flex items-center justify-center w-full h-full">
                                    <span>{collapsedAbbrev}</span>
                                    {hasTooltip && (
                                      <ChatBubbleBottomCenterTextIcon
                                        onClick={(e) => { e.stopPropagation(); openNotesModal(driver.driver, driver.driver_name); }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="w-3 h-3 absolute top-0 right-0 text-muted cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400"
                                      />
                                    )}
                                  </div>
                                </Tooltip>
                              </td>
                            );
                          }

                          return (
                            <td
                              key={index}
                              onClick={isClickable ? handleCellClick : undefined}
                              onMouseDown={(e) => handleCellMouseDown(e, driver.driver, dateKey, isSelectable)}
                              onMouseEnter={() => handleCellMouseEnter(driver.driver, dateKey, isSelectable)}
                              className={`px-1 py-0.5 text-center text-xs border ${hasConflict ? 'border-2 border-red-500 dark:border-red-400' : 'border-rule'} ${getStatusStyle(status, statusInfo, driver.region)} ${todayBg} ${isClickable ? `cursor-pointer ${hoverClass}` : ''} ${isOverdue ? 'italic' : ''} ${selectionStyle} select-none`}
                              style={{ width: expandedColumnWidth }}
                            >
                              <div className="group/notecell relative inline-flex items-center justify-center w-full">
                                {displayContent}
                                {hasTooltip ? (
                                  <Tooltip
                                    content={tooltipContent}
                                    placement="top"
                                  >
                                    <ChatBubbleBottomCenterTextIcon
                                      onClick={(e) => { e.stopPropagation(); openNotesModal(driver.driver, driver.driver_name); }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      className="w-4 h-4 absolute top-0 right-0 text-muted cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400"
                                    />
                                  </Tooltip>
                                ) : (
                                  // No note yet: reveal a faint icon on cell hover so a personal note can be added.
                                  <ChatBubbleBottomCenterTextIcon
                                    onClick={(e) => { e.stopPropagation(); openNotesModal(driver.driver, driver.driver_name); }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    title="Add a personal note"
                                    className="w-4 h-4 absolute top-0 right-0 text-gray-300 dark:text-gray-600 opacity-0 group-hover/notecell:opacity-100 cursor-pointer hover:!text-indigo-600 dark:hover:!text-indigo-400 transition-opacity"
                                  />
                                )}
                              </div>
                                    </td>
                                  );
                                })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {/* Sticky Footer with Statistics */}
          <tfoot className="bg-slate-600 dark:bg-slate-700 text-white sticky bottom-0 z-10">
            {/* Daily Available Row */}
            <tr>
              <td className="w-5 border-r border-slate-500"></td>
              <td className="px-2 py-1 text-center font-semibold text-xs border-r border-slate-500">
                Daily Available
              </td>
              <td className="border-r border-slate-500 overflow-hidden" style={{ width: showWorkingQuals ? WORKING_QUALS_COL_WIDTH : 0, padding: 0 }}></td>
              {dailyStats.map((stat, index) => {
                const isToday = formatDateKey(weekDays[index]) === formatLocalDateKey(new Date());
                const isCollapsed = collapsedDays.has(index);
                return (
                  <td
                    key={index}
                    className={`px-2 py-1 text-center font-semibold text-xs ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px]' : ''} ${index < 6 ? 'border-r border-slate-500' : ''} ${isToday ? 'bg-slate-800' : ''}`}
                    style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                  >
                    {!isCollapsed && (<>{stat.availableCount}/{stat.totalCount}</>)}
                  </td>
                );
              })}
            </tr>
            {/* Totals Percentage Row */}
            <tr>
              <td className="w-5 border-r border-slate-500"></td>
              <td className="px-2 py-1 text-center font-semibold text-xs border-r border-slate-500">
                Totals
              </td>
              <td className="border-r border-slate-500 overflow-hidden" style={{ width: showWorkingQuals ? WORKING_QUALS_COL_WIDTH : 0, padding: 0 }}></td>
              {dailyStats.map((stat, index) => {
                // Determine color based on percentage: green if >= 85%, red if < 85%
                const bgColor = stat.percentage >= 85 ? 'bg-green-500' : 'bg-red-500';
                const isCollapsed = collapsedDays.has(index);
                return (
                  <td
                    key={index}
                    className={`px-2 py-1 text-center font-semibold text-xs ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px] bg-slate-600 dark:bg-slate-700' : `${index < 6 ? 'border-r border-slate-500' : ''} ${bgColor}`}`}
                    style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                  >
                    {!isCollapsed && (<>{stat.percentage.toFixed(2)}%</>)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
          </div>
          </div>
            );
          })()
        ) : activeTab === 'declined-moves' ? (
          <DriverDeclinedMoves
            selectedDispatchers={selectedDispatchers}
            selectedRegions={selectedRegions}
            selectedDate={declinedMovesDate}
            refreshTrigger={refreshTrigger}
            groupByDispatcher={groupByDispatcher}
            usernameToDisplayname={usernameToDisplayname}
          />
        ) : (
          <DriverPreferencesBans
            selectedDispatchers={selectedDispatchers}
            selectedRegions={selectedRegions}
            refreshTrigger={refreshTrigger}
            usernameToDisplayname={usernameToDisplayname}
          />
        )}
      </div>

      {/* Status Update Modal */}
      <InfoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`${modalDriver} ${modalDriverName}`}
        maxWidth="sm"
        body={
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Date: {modalDate ? new Date(modalDate + 'T00:00:00').toLocaleDateString() : ''}
              </p>
            </div>
            <div>
              <span className="block text-sm font-medium text-label mb-2">Status</span>
              <span className="isolate inline-flex rounded-md shadow-sm w-full">
                <button
                  type="button"
                  onClick={() => setModalWorking('NOT_WORKING')}
                  className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold rounded-l-md ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    modalWorking === 'NOT_WORKING'
                      ? 'bg-info text-info-fg ring-blue-200'
                      : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                  }`}
                >
                  Not Working
                </button>
                <button
                  type="button"
                  onClick={() => setModalWorking('UNKNOWN')}
                  className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold -ml-px ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                    modalWorking === 'UNKNOWN'
                      ? 'bg-gray-200 text-gray-700 ring-gray-300 dark:bg-gray-600 dark:text-gray-100 dark:ring-gray-500'
                      : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                  }`}
                >
                  Unknown
                </button>
                <button
                  type="button"
                  onClick={() => setModalWorking('WORKING')}
                  className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold -ml-px rounded-r-md ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-green-400 ${
                    modalWorking === 'WORKING'
                      ? 'bg-green-100 text-green-700 ring-green-200'
                      : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                  }`}
                >
                  Working
                </button>
              </span>
            </div>
            {modalWorking === 'WORKING' && (
              <div>
                <TypeAhead
                  label="Working Category"
                  options={WORKING_CATEGORIES.map(c => ({ value: c.value, label: c.label }))}
                  value={modalWorkingCategory}
                  onChange={setModalWorkingCategory}
                  placeholder="Select working category (optional)"
                  showSearch={true}
                />
              </div>
            )}
            {modalWorking === 'WORKING' && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-label">
                  Confirmed
                </span>
                <Toggle
                  checked={modalConfirmed}
                  onChange={setModalConfirmed}
                  size="md"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-label mb-1">
                Comments
              </label>
              <textarea
                value={modalComment}
                onChange={(e) => setModalComment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Optional comments..."
              />
            </div>
            {(modalChangedBy || modalChangedDateTime) && (
              <div className="text-xs text-muted pt-2">
                {modalChangedBy && <>Last changed by: {modalChangedBy}</>}
                {modalChangedBy && modalChangedDateTime && ' - '}
                {modalChangedDateTime && <>{modalChangedDateTime}</>}
              </div>
            )}
          </div>
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: () => setModalOpen(false),
            variant: 'secondary'
          },
          {
            label: saving ? 'Saving...' : 'Save',
            onClick: saveStatus,
            variant: 'primary',
            disabled: saving
          }
        ]}
      />

      {/* Personal Notes Modal (private to the logged-in user) */}
      <InfoModal
        isOpen={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        title={`Notes — ${notesModalDriver} ${notesModalDriverName}`}
        maxWidth="sm"
        body={
          <div className="space-y-3">
            <p className="text-xs text-muted">
              This note is private — only you can see it.
            </p>
            <div>
              <label className="block text-sm font-medium text-label mb-1">
                My Note
              </label>
              <textarea
                value={notesModalText}
                onChange={(e) => setNotesModalText(e.target.value)}
                rows={4}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="e.g. Tried calling, left voicemail..."
              />
            </div>
          </div>
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: () => setNotesModalOpen(false),
            variant: 'secondary'
          },
          ...(driverNotes[notesModalDriver] ? [{
            label: 'Delete',
            onClick: deleteNote,
            variant: 'danger' as const,
            disabled: notesSaving
          }] : []),
          {
            label: notesSaving ? 'Saving...' : 'Save',
            onClick: saveNote,
            variant: 'primary' as const,
            disabled: notesSaving
          }
        ]}
      />

      {/* Pro Details Drawer */}
      <ProDetailsDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerLegs([]);
        }}
        driver={drawerDriver}
        driverName={drawerDriverName}
        date={drawerDate}
        legs={drawerLegs}
        loading={loadingLegs}
        initialConfirmed={drawerConfirmed}
        changedBy={drawerChangedBy}
        changedDateTime={drawerChangedDateTime}
        onClearStatus={clearStatus}
        hasExistingStatus={drawerHasExistingStatus}
        initialComment={drawerComment}
        initialStatus={drawerStatus}
        initialWorkingCategory={drawerWorkingCategory}
        showStatusSelector={true}
        onStatusSave={saveDrawerStatus}
        onProClick={openOrderDrawer}
        noBackdrop
      />

      {/* Order Details Drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />

      {/* Bulk Update Modal */}
      <InfoModal
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        title={`Bulk Update Status - ${selectedCells.size} Cell${selectedCells.size !== 1 ? 's' : ''}`}
        maxWidth="md"
        body={
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h4 className="text-sm font-medium text-label mb-2">
                Update Summary
              </h4>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p>
                  <span className="font-medium">{getSelectedCellsInfo().drivers.size}</span> driver{getSelectedCellsInfo().drivers.size !== 1 ? 's' : ''} × <span className="font-medium">{getSelectedCellsInfo().dates.size}</span> day{getSelectedCellsInfo().dates.size !== 1 ? 's' : ''}
                </p>
                <p>
                  Total updates: <span className="font-medium">{selectedCells.size}</span>
                </p>
              </div>
            </div>
            <div>
              <span className="block text-sm font-medium text-label mb-2">Status</span>
              <span className="isolate inline-flex rounded-md shadow-sm w-full">
                <button
                  type="button"
                  onClick={() => setBulkStatus('NOT_WORKING')}
                  className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold rounded-l-md ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    bulkStatus === 'NOT_WORKING'
                      ? 'bg-info text-info-fg ring-blue-200'
                      : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                  }`}
                >
                  Not Working
                </button>
                <button
                  type="button"
                  onClick={() => setBulkStatus('UNKNOWN')}
                  className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold -ml-px ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                    bulkStatus === 'UNKNOWN'
                      ? 'bg-gray-200 text-gray-700 ring-gray-300 dark:bg-gray-600 dark:text-gray-100 dark:ring-gray-500'
                      : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                  }`}
                >
                  Unknown
                </button>
                <button
                  type="button"
                  onClick={() => setBulkStatus('WORKING')}
                  className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold -ml-px rounded-r-md ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-green-400 ${
                    bulkStatus === 'WORKING'
                      ? 'bg-green-100 text-green-700 ring-green-200'
                      : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                  }`}
                >
                  Working
                </button>
              </span>
            </div>
            {bulkStatus === 'WORKING' && (
              <div>
                <TypeAhead
                  label="Working Category"
                  options={WORKING_CATEGORIES.map(c => ({ value: c.value, label: c.label }))}
                  value={bulkWorkingCategory}
                  onChange={setBulkWorkingCategory}
                  placeholder="Select working category (optional)"
                  showSearch={true}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-label mb-1">
                Comments (applies to all)
              </label>
              <textarea
                value={bulkComment}
                onChange={(e) => setBulkComment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Optional comments for all selected cells..."
              />
            </div>
          </div>
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: () => setBulkModalOpen(false),
            variant: 'secondary'
          },
          {
            label: bulkSaving ? 'Saving...' : 'Save All',
            onClick: saveBulkStatus,
            variant: 'primary',
            disabled: bulkSaving
          }
        ]}
      />

      {/* Parking Location Modal */}
      <ParkingLocationModal
        isOpen={parkingModalOpen}
        onClose={() => setParkingModalOpen(false)}
        driver={parkingModalDriver}
        driverName={parkingModalDriverName}
        currentParkingloc={parkingModalCurrentLoc}
        currentParkingName={parkingModalCurrentName}
        currentParkingColor={parkingModalCurrentColor}
        onSave={handleParkingLocationSave}
      />
    </div>
  );
};

export default Drivers;
